import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Theme,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import clsx from 'clsx';

import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SaveIcon from '@mui/icons-material/Save';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import LinkIcon from '@mui/icons-material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import SyncIcon from '@mui/icons-material/Sync';
import LinkOffIcon from '@mui/icons-material/LinkOff';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';

import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import CommonButton from '../../components/buttons/CommonButton';
import InactiveUserName from '../../components/InactiveUserName';
import Loader from '../../components/Loader';
import FormTextField from '../../components/formFields/FormTextField';
import FormMaskedTextField from '../../components/formFields/FormMaskedTextField';
import FormSelectField from '../../components/formFields/FormSelectField';
import FormCheckboxField from '../../components/formFields/FormCheckboxField';
import AddressAutocomplete from '../../components/formFields/AddressAutocomplete';
import VendorConfirmationPopup, { VendorOption } from '../../components/VendorConfirmationPopup';
import FindNetSuiteVendorPopup from '../../components/FindNetSuiteVendorPopup';
import ConfirmationPopup from '../../components/ConfirmationPopup';
import { EditVendorAccountFormSchema } from '../../helpers/formSchemas/formSchemas';
import { VENDOR_ACCOUNT_EDIT_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import {
  US_STATES_LIST,
  VENDOR_TYPE_OPTIONS,
} from '../../constants/constants';

import { AddressPrediction, validateAddress } from '../../api/addressApi/addressApi';
import { addressOptionsQuery, mapAddressResults } from './helpers';
import useSnackBar from '../../hooks/useSnackBar';
import { useHasPermission } from '../../hooks/useHasPermission';
import {
  ErrorData,
  EwsStatus,
  VendorAchAccount,
  VendorAccountDetail,
  VendorCheckAccount,
  // vendorFBO
} from '../../types';
import { VendorAccountSubmitFormProps } from './types';
import { StatusChip } from '../../components/StatusChip';
import {
  EwsValidationStatusMap,
  ExternalAccountStatusMap,
  ProvisioningStatusMap,
  // FBOAccountStatusMap
} from '../FirmBankingView/constants';
import {
  addACHAccount,
  addCheckAccount,
  adminConfirmationEmail,
  deleteACHAccount,
  deleteCheckAccount,
  editACHAccount,
  editCheckAccount,
  getAchAccountNumber,
  getBankRoutingDetails,
  getVendorAccount,
  resendConfirmationEmail,
  retryProvisioning,
  updateVendorAccount,
  setVendorExternalVendorId,
  removeExternalVendorId,
  //  getVendorFBO
} from '../../api/vendorApi/vendorAccountsApi';
import { AxiosError } from 'axios';
import ViewVendorChangesPopup from '../../components/viewVendorChangesPopup';
import VendorDocumentTab from './VendorDocumentTab';
import VendorCommentsSection from './VendorCommentsSection';
import AchEwsNote from './AchEwsNote';
import theme from '../../theme';
import moment from 'moment';
import PaymentHistoryTable from './PaymentHistoryTable';
// import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';

declare global {
  interface Window {
    checkCustomAddresses?: () => boolean;
  }
}

type DisplayAchAccount = {
  id: string;
  nickname: string;
  accountType: string;
  routingNumber: string;
  maskedAccountNumber: string;
  accountNumber: string;
  isDefault: boolean;
  provisionStatus: ProvisioningStatusKey;
  provisionError: string | null;
  ewsStatus?: EwsStatus | null;
};

type DisplayCheckAccount = {
  id: string;
  nickname: string;
  payeeName: string;
  recipientName: string;
  address: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  isDefault: boolean;
  mailingPhoneNumber: string;
  provisionStatus: ProvisioningStatusKey;
  provisionError: string | null;
};

type DefaultPaymentMethod = 'ACH' | 'CHECK' | 'V';
type ProvisioningStatusKey = 'NotStarted' | 'InProgress' | 'Error' | 'CriticalError' | 'Completed';
type ProvisioningAccountMeta = {
  provisionStatus?: string | null;
  provisionError?: string | null;
};

const checkMarkURI =
  // eslint-disable-next-line quotes
  "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E";

const CheckBoxIcon = styled('span')(() => ({
  borderRadius: 8,
  width: 24,
  height: 24,
  backgroundColor: 'transparent',
  border: '2px solid #4A4E5D',
}));

const CheckedCheckBoxIcon = styled(CheckBoxIcon)({
  backgroundColor: '#00667C',
  border: '2px solid #00667C',
  '&:before': {
    display: 'block',
    width: 24,
    height: 24,
    backgroundImage: `url("${checkMarkURI}")`,
    content: '""',
  },
});

const useStyles = makeStyles()((theme: Theme) => ({
  viewContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 19px',
    width: '902px', // Figma width
    maxWidth: '100%', // Responsive for smaller screens
    marginBottom: '20px', // Center horizontally and give top spacing
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0px 4px 8.3px rgba(0, 0, 0, 0.1)',
    borderRadius: '12px',
    '@media (max-width: 950px)': {
      width: '100%',
      padding: '6px 12px',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '12px',
    },
  },
  warningSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
    '@media (max-width: 950px)': {
      width: '100%',
    },
  },
  viewButton: {
    backgroundColor: '#00667C',
    color: '#FFFFFF',
    borderRadius: '12px',
    padding: '10px 16px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    '&:hover': {
      backgroundColor: '#00667C',
    },
    '@media (max-width: 950px)': {
      alignSelf: 'flex-end',
    },
  },
  warningText: {
    fontFamily: 'Inter',
    fontWeight: 600,
    lineHeight: '120%',
    color: '#C3C4C8',
  },

  errorMessageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: '16px',
    gap: '8px',
    background: theme.palette.primary.light,
    width: '100%',
    margin: '12px 0',
    boxSizing: 'border-box',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    wordBreak: 'break-word',
  },

  errorMessageText: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '120%',
    color: theme.palette.error.main,
    margin: '7px',
  },

  warningMessageText: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '120%',
    color: theme.palette.warning.main,
    margin: '7px',
  },

  buttonDefault: {
    marginTop: '8px',
    height: '30px',
    maxWidth: '70px',
    width: '100%',
    borderRadius: '8px',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: '500',
    textTransform: 'none',
    color: theme.palette.common.white,

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.08)',
      background: 'rgba(255, 255, 255, 0.08)',
    },

    '@media (max-width: 550px)': {
      maxWidth: '100%',
    },
  },
  retryButtonDefault: {
    height: '30px',
    width: 'auto',
    flexShrink: 0,
    padding: '0 16px',
    borderRadius: '8px',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: '500',
    textTransform: 'none',
    color: theme.palette.common.white,

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.08)',
      background: 'rgba(255, 255, 255, 0.08)',
    },
  },
  saveButton: {
    background: theme.palette.info.main,

    '&:hover': {
      background: theme.palette.info.main,
    },
  },
  vendorEditForm: {
    padding: '32px',
    flexBasis: '896px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',
    '&, & *': {
      fontFamily: 'Inter',
    },

    '@media (max-width: 1100px)': {
      flexBasis: 'auto',
    },

    '@media (max-width: 725px)': {
      flexBasis: '520px',
      padding: '16px',
    },
  },
  formFieldSystem: {
    '& .MuiInputLabel-root': {
      fontFamily: 'Inter',
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '120%',
      letterSpacing: '0px',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: `${theme.palette.common.white} !important`,
    },
    '& .MuiOutlinedInput-root': {
      minHeight: '54px',
      borderRadius: '12px',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '120%',
    },
    '& .MuiSelect-select': {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '120%',
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Inter',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '120%',
    },
  },
  container: {
    display: 'flex',
    padding: '32px',
    borderRadius: '16px',
    backgroundColor: theme.palette.primary.light,
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '40px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    fontSize: '34px',
    fontWeight: 600,
    lineHeight: '120%',
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },
  tabsContainer: {
    '&, & *': {
      fontFamily: 'Inter',
    },
    '& .MuiTabs-root': {
      minHeight: 'auto',
    },
    '& .MuiTab-root': {
      color: theme.palette.text.disabled,
      fontSize: '20px',
      fontWeight: 500,
      fontFamily: 'Inter',
      textTransform: 'capitalize',
      minHeight: '48px',
      '&.Mui-selected': {
        color: theme.palette.common.white,
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.info.main,
    },
  },
  formContainer: {
    display: 'flex',
    width: '100%',
    gap: '32px',
    paddingBottom: '20px',

    '@media (max-width: 1100px)': {
      flexDirection: 'column',
    },
  },
  formButtonsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    maxWidth: '416px',
    gap: '16px',

    '@media (max-width: 768px)': {
      alignSelf: 'flex-start',
    },
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  blockingOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(10, 14, 24, 0.32)',
    backdropFilter: 'blur(1.5px)',
  },
  tabContent: {
    width: '100%',
  },
  tabContentFbo: {
    width: '30%',
    backgroundColor: theme.palette.primary.light,
  },
  statusContainer: {
    display: 'flex',
    gap: '48px',
    width: '100%',

    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gap: '24px',
    },
  },
  statusSection: {
    flex: 1,
    border: '1px solid #5e6174',
    borderRadius: '8px',
    padding: '16px',
  },
  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    margin: '0px',
  },
  formWrapper: {
    display: 'flex',
  },
  searchVendorButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px 16px',
    border: '1px solid #5e6174',
    borderRadius: '10px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    minHeight: '54px',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  accordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderRadius: '10px',
    marginBottom: '16px',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 0 16px 0',
    },
  },
  accordionSummary: {
    backgroundColor: 'transparent',
    minHeight: '56px',
    '&.Mui-expanded': {
      minHeight: '56px',
    },
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: 0,
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      order: -1,
      marginRight: '12px',
    },
  },
  accordionDetails: {
    padding: '16px 24px 24px',
    backgroundColor: 'transparent',
  },
  paymentMethodsPanel: {
    marginTop: '8px',
    padding: 0,
    borderRadius: 0,
    border: 'none',
    background: 'transparent',
  },
  paymentMethodsHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  paymentMethodsNote: {
    marginTop: '8px',
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
    fontSize: '12px',
    lineHeight: '18px',
  },
  paymentMethodsStack: {
    marginTop: '14px',
    position: 'relative',
  },
  paymentAccordion: {
    border: '1px solid rgb(0, 102, 124)',
    borderRadius: '12px',
    background:
      'linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
    overflow: 'hidden',
    transition: 'border-color 220ms ease, transform 220ms ease',
    '&.Mui-expanded': {
      borderColor: 'rgb(0, 102, 124)',
      transform: 'translateY(-1px)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  paymentAccordionAch: {
    '& .MuiAccordionSummary-root': {
      borderLeft: '4px solid rgb(0, 102, 124)',
    },
  },
  paymentAccordionCheck: {
    '& .MuiAccordionSummary-root': {
      borderLeft: '4px solid rgb(0, 102, 124)',
    },
  },
  paymentAccordionUpcoming: {
    borderColor: 'rgb(0, 102, 124)',
    '& .MuiAccordionSummary-root': {
      borderLeft: '4px solid rgb(0, 102, 124)',
    },
  },
  paymentSummaryTitleWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  paymentSummaryTitle: {
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: '14px',
    fontFamily: 'Space Grotesk, Inter',
    letterSpacing: '0.02em',
  },
  paymentSummaryCount: {
    color: '#C6D4EA',
    fontSize: '12px',
    fontFamily: 'Inter',
    fontWeight: 500,
    lineHeight: '16px',
  },
  paymentSummaryMeta: {
    color: 'rgba(226, 232, 240, 0.72)',
    fontSize: '11px',
    fontFamily: 'Inter',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  paymentDefaultWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  paymentDefaultLabel: {
    color: '#C6D4EA',
    fontSize: '11px',
    fontFamily: 'Inter',
    letterSpacing: '0.05em',
    textTransform: 'none',
  },
  paymentDefaultRadio: {
    color: '#00667c',
    '&.Mui-checked': {
      color: '#00667c',
    },
    padding: '4px',
  },
  paymentAccordionDetails: {
    padding: '16px',
  },
  paymentSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  paymentSectionLabel: {
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 500,
  },
  paymentInlineAction: {
    color: '#57D8FF',
    fontFamily: 'Space Grotesk, Inter',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    cursor: 'pointer',
    '&:hover': {
      color: '#57D8FF',
      textDecoration: 'none',
    },
  },
  paymentInlineActionToggle: {
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      color: '#FFFFFF',
      textDecoration: 'none',
    },
  },
  paymentInlineActionAdd: {
    color: '#FFFFFF',
    '&:hover': {
      color: '#FFFFFF',
      textDecoration: 'none',
    },
  },
  paymentAccountCard: {
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '10px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    background: 'rgba(255, 255, 255, 0.03)',
  },
  paymentCardTitle: {
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Inter',
    lineHeight: '20px',
    wordBreak: 'break-word',
  },
  paymentCardHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  paymentCardText: {
    color: '#BFCDE2',
    fontSize: '13px',
    fontFamily: 'Inter',
  },
  paymentDefaultText: {
    color: '#1FC9A8',
    fontSize: '12px',
    fontWeight: 700,
    fontFamily: 'Inter',
    letterSpacing: '0.04em',
    textTransform: 'none',
  },
  paymentCardActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '4px',
  },
  paymentStatusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginTop: 0,
    flexWrap: 'wrap',
  },
  paymentProvisionError: {
    color: theme.palette.error.main,
    fontSize: '12px',
    lineHeight: '16px',
    fontFamily: 'Inter',
    marginTop: '2px',
    wordBreak: 'break-word',
  },
  paymentMoreToggle: {
    color: '#57D8FF',
    fontFamily: 'Inter',
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    width: 'fit-content',
    '&:hover': {
      color: '#57D8FF',
    },
  },
  paymentAddForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.02)',
    padding: '12px',
  },
  paymentAddRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  // Matches the shared FormTextField / FormSelectField look used by the Vendor Details fields
  paymentAddInput: {
    width: '100%',
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '12px',
    '& .MuiOutlinedInput-input': {
      padding: '13.5px 16px',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      fontSize: '14px',
      lineHeight: '120%',
      fontWeight: 500,
      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
        WebkitTextFillColor: theme.palette.text.disabled,
      },
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
    },
    '& .MuiSelect-icon': {
      color: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.disabled,
      borderRadius: '12px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.dark,
        borderRadius: '12px',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.info.main,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.info.main,
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.error.main,
      },
      '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderWidth: 0,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.disabled,
      '&[data-shrink="false"]': {
        top: '-3px',
      },
      '&.Mui-error': {
        color: theme.palette.error.main,
      },
      '&.Mui-focused': {
        color: theme.palette.common.white,
      },
      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },
    '& .MuiFormHelperText-root': {
      marginTop: '6px',
      marginLeft: 0,
      minHeight: '18px',
      lineHeight: '18px',
    },
  },
  paymentAddActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  // Same size and font as paymentAddCancel so the two action buttons match
  paymentAddPrimary: {
    backgroundColor: theme.palette.info.main,
    border: `1px solid ${theme.palette.info.main}`,
    color: theme.palette.common.white,
    textTransform: 'none',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '16px',
    borderRadius: '12px',
    minHeight: '48px',
    padding: '10px 16px',
    '&:hover': {
      backgroundColor: theme.palette.info.main,
    },
    '&.Mui-disabled': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.info.main,
      opacity: 0.45,
    },
  },
  paymentAddCancel: {
    background: theme.palette.info.dark,
    border: `1px solid ${theme.palette.info.dark}`,
    color: theme.palette.common.white,
    textTransform: 'none',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '16px',
    borderRadius: '12px',
    minHeight: '48px',
    padding: '10px 16px',
    '&:hover': {
      background: theme.palette.info.dark,
      border: `1px solid ${theme.palette.info.dark}`,
      color: theme.palette.common.white,
    },
  },
  paymentComingSoon: {
    color: '#8f93a6',
    fontSize: '12px',
    fontFamily: 'Inter',
  },
  verifyButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'Inter',
    '&:hover': {
      opacity: 1,
    },
    padding: '7px 14px 7px 14px',
    borderRadius: '8px',
    backgroundColor: theme.palette.info.main,
  },
  retryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'Inter',
    '&:hover': {
      opacity: 1,
    },
    padding: '7px 14px 7px 14px',
    borderRadius: '8px',
    backgroundColor: theme.palette.info.main,
  },
  checkboxWrapper: {
    width: '100%',
    marginLeft: 0,
    '& .MuiCheckbox-root': {
      padding: 0,
      paddingRight: '8px',
    },
    '& .MuiFormControlLabel-label': {
      color: theme.palette.text.secondary,
      fontFamily: 'Inter',
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '120%',
      letterSpacing: '0px',
    },
  },
  defaultMethodPill: {
    backgroundColor: '#00667c',
    color: theme.palette.common.white,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'none',
    padding: '4px 10px',
    height: '24px',
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '168px',
    width: '100%',
    padding: '10px',
  },
  actionsHeading: {
    color: theme.palette.common.white,
    fontSize: '20px',
    fontFamily: 'Poppins',
    fontWeight: 700,
    paddingBottom: '12px',
  },
  actionButtonsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  confirmPopupActionButton: {
    borderRadius: '12px',
    padding: '10px 16px',
    minHeight: '48px',
    minWidth: '132px',
    textTransform: 'capitalize',
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 500,
  },
  confirmPopupDeleteButton: {
    background: theme.palette.info.main,
    color: theme.palette.common.white,
    '&:hover': {
      background: theme.palette.info.main,
      color: theme.palette.common.white,
    },
  },
  confirmPopupCancelButton: {
    background: theme.palette.info.dark,
    border: `1px solid ${theme.palette.info.dark}`,
    color: theme.palette.common.white,
    '&:hover': {
      background: theme.palette.info.dark,
      border: `1px solid ${theme.palette.info.dark}`,
      color: theme.palette.common.white,
    },
  },
  field: {
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '19.2px',
    color: theme.palette.common.white,
  },
  infoIconButton: {
    color: theme.palette.text.secondary,
    padding: '2px',
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
}));

const VendorEditFormContainer = () => {
  const ROUTING_LOOKUP_UNAVAILABLE_MESSAGE =
    'Unable to verify bank name right now. You can still continue with a valid routing number.';
  const DEFAULT_VISIBLE_ACCOUNTS = 2;
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();
  const navigate = useNavigate();
  const { accountId } = useParams<{ accountId: string }>();
  const canEdit = useHasPermission('Vendors', 'Edit');
  const vendorEntity = localStorage.getItem('accountingSystem') === 'NETSUITE' ? 'NetSuite' : 'QuickBooks';

  const maskAccount = (value?: string) => {
    if (!value) return '****';
    const last4 = value.slice(-4);
    return `****${last4}`;
  };

  const validateRoutingChecksum = (routing: string): boolean => {
    if (routing.length !== 9) return false;

    const d = routing.split('').map(Number);
    const checksum = (
      3 * (d[0] + d[3] + d[6]) +
      7 * (d[1] + d[4] + d[7]) +
      1 * (d[2] + d[5] + d[8])
    ) % 10;

    return checksum === 0;
  };

  const isValidRoutingNumber = (routing: string): boolean => {
    if (!/^\d{9}$/.test(routing)) return false;
    return validateRoutingChecksum(routing);
  };

  const isValidAccountNumber = (account: string): boolean => {
    return /^\d{4,17}$/.test(account) && !/^0+$/.test(account);
  };

  const getProvisioningStatusForCard = (
    status?: string | null,
  ): ProvisioningStatusKey => {
    if (status === 'NotStarted') return 'NotStarted';
    if (status === 'InProgress') return 'InProgress';
    if (status === 'Error') return 'Error';
    if (status === 'CriticalError') return 'CriticalError';
    if (status === 'Completed') return 'Completed';
    return 'NotStarted';
  };

  const shouldShowProvisionError = (status: ProvisioningStatusKey) =>
    status === 'Error' || status === 'CriticalError';

  const normalizeUsPhoneNumber = (value?: string) => {
    const digits = (value || '').replace(/\D/g, '');
    if (!digits) return '';
    const tenDigits = digits.length >= 10 ? digits.slice(-10) : digits;
    return tenDigits.length === 10 ? `+1${tenDigits}` : value?.trim() || '';
  };

  const formatUsPhoneInput = (value?: string) => {
    const digits = (value || '').replace(/\D/g, '');
    if (!digits) return '';

    // Keep only US country code + local number and render in +1-XXX-XXX-XXXX mask.
    const normalized = digits.startsWith('1') ? digits.slice(1) : digits;
    const local = normalized.slice(0, 10);

    if (local.length <= 3) return `+1-${local}`;
    if (local.length <= 6) return `+1-${local.slice(0, 3)}-${local.slice(3)}`;
    return `+1-${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
  };

  const [data, setData] = useState<VendorAccountDetail | null>(null);
  // const [fboData, setFboData] = useState<vendorFBO | null>(null);
  const [historyData, setHistoryData] = useState<VendorAccountDetail | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [pendingSubmissionData, setPendingSubmissionData] =
    useState<VendorAccountSubmitFormProps | null>(null);
  const [vendorConfirmationOpen, setVendorConfirmationOpen] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewChangesPopupOpen, setViewChangesPopupOpen] = useState(false);
  const [showAllAchAccounts, setShowAllAchAccounts] = useState(false);
  const [showAllCheckAccounts, setShowAllCheckAccounts] = useState(false);
  const [manualAchAccounts, setManualAchAccounts] = useState<DisplayAchAccount[]>([]);
  const [editedAchAccounts, setEditedAchAccounts] = useState<DisplayAchAccount[]>([]);
  const [deletedAchIds] = useState<string[]>([]);
  const [manualCheckAccounts, setManualCheckAccounts] = useState<DisplayCheckAccount[]>([]);
  const [deletedCheckIds] = useState<string[]>([]);
  const [editedCheckAccounts, setEditedCheckAccounts] = useState<DisplayCheckAccount[]>([]);
  const [achDefaultOverrideId, setAchDefaultOverrideId] = useState<string | null>(null);
  const [checkDefaultOverrideId, setCheckDefaultOverrideId] = useState<string | null>(null);
  const [isAddingAch, setIsAddingAch] = useState(false);
  const [isAddingCheck, setIsAddingCheck] = useState(false);
  const [isSavingAchAccount, setIsSavingAchAccount] = useState(false);
  const [isSavingCheckAddress, setIsSavingCheckAddress] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isPaymentMethodsRefreshing, setIsPaymentMethodsRefreshing] = useState(false);
  const [editingAchId, setEditingAchId] = useState<string | null>(null);
  const [editingCheckId, setEditingCheckId] = useState<string | null>(null);
  const [achDraft, setAchDraft] = useState({
    nickname: '',
    accountType: 'Checking',
    routingNumber: '',
    accountNumber: '',
  });
  const [achDraftTouched, setAchDraftTouched] = useState({
    nickname: false,
    accountNumber: false,
    routingNumber: false,
  });
  const [isAchDraftSubmitAttempted, setIsAchDraftSubmitAttempted] = useState(false);
  const [achRoutingBankName, setAchRoutingBankName] = useState<string | null>(null);
  const [achRoutingLookupError, setAchRoutingLookupError] = useState<string | null>(null);
  const latestAchRoutingRef = useRef('');
  const achRoutingLookupCacheRef = useRef<Record<string, string | null>>({});
  const achRoutingLookupFailedCacheRef = useRef<Record<string, boolean>>({});
  const [checkDraft, setCheckDraft] = useState({
    nickname: '',
    payeeName: '',
    recipientName: '',
    address: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    mailingPhoneNumber: '',
  });
  const [checkDraftAddressKey, setCheckDraftAddressKey] = useState(0);
  const [checkDraftAddressVerified, setCheckDraftAddressVerified] = useState(false);
  const [checkDraftAddressKeptAsEntered, setCheckDraftAddressKeptAsEntered] = useState(false);
  const [isCheckDraftAddressVerifying, setIsCheckDraftAddressVerifying] = useState(false);
  const [checkDraftSameAsParticipationAddress, setCheckDraftSameAsParticipationAddress] = useState(false);
  const [isSyncingCheckDraftFromParticipation, setIsSyncingCheckDraftFromParticipation] =
    useState(false);
  const [checkDraftTouched, setCheckDraftTouched] = useState({
    nickname: false,
    address: false,
    city: false,
    state: false,
    zip: false,
    mailingPhoneNumber: false,
  });
  const [isCheckDraftSubmitAttempted, setIsCheckDraftSubmitAttempted] = useState(false);
  const [isCheckDraftPayeeManuallyEdited, setIsCheckDraftPayeeManuallyEdited] = useState(false);
  const [isCheckDraftRecipientManuallyEdited, setIsCheckDraftRecipientManuallyEdited] =
    useState(false);
  const [paymentMethodDeleteConfirmationOpen, setPaymentMethodDeleteConfirmationOpen] =
    useState(false);
  const [pendingDeletePaymentMethod, setPendingDeletePaymentMethod] = useState<{
    type: 'ach' | 'check';
    id: string;
  } | null>(null);

  const baseAchAccounts: DisplayAchAccount[] =
    data?.achAccounts?.map((account) => ({
      id: String(account.id),
      nickname: account.nickname || '',
      accountType: account.accountType || 'Checking',
      routingNumber: account.routingNumber || '',
      maskedAccountNumber:
        account.maskedAccountNumber || maskAccount(account.accountNumber || account.accountNumberLast4 || ''),
      isDefault: account.isDefault,
      accountNumber: account.accountNumber || account.accountNumberLast4 || '',
      provisionStatus: getProvisioningStatusForCard(
        (account as VendorAchAccount & ProvisioningAccountMeta).provisionStatus,
      ),
      provisionError: (account as VendorAchAccount & ProvisioningAccountMeta).provisionError || null,
      ewsStatus: account.ewsStatus ?? null,
    })) || [];

  const filteredBaseAchAccounts = baseAchAccounts.filter((account) => !deletedAchIds.includes(account.id));

  const baseCheckAccounts: DisplayCheckAccount[] =
    data?.checkAccounts?.map((account) => ({
      id: String(account.id),
      nickname: account.nickname,
      payeeName: (account.payeeName || '').trim() || data.name || '',
      recipientName:
        (account.recipientName || '').trim() || data.contactName || data.name || '',
      address: account.address,
      addressLine2: account.addressLine2 || '',
      city: account.city,
      state: account.state,
      zip: account.zip,
      country: account.country || 'US',
      isDefault: account.isDefault,
      mailingPhoneNumber: account.mailingPhoneNumber || '',
      provisionStatus: getProvisioningStatusForCard(
        (account as VendorCheckAccount & ProvisioningAccountMeta).provisionStatus,
      ),
      provisionError:
        (account as VendorCheckAccount & ProvisioningAccountMeta).provisionError || null,
    })) || [];

  const filteredBaseCheckAccounts = baseCheckAccounts.filter(
    (account) => !deletedCheckIds.includes(account.id),
  );

  const mergedBaseAchAccounts = filteredBaseAchAccounts.map((account) => {
    const editedAccount = editedAchAccounts.find((item) => item.id === account.id);
    return editedAccount || account;
  });

  const mergedBaseCheckAccounts = filteredBaseCheckAccounts.map((account) => {
    const editedAccount = editedCheckAccounts.find((item) => item.id === account.id);
    return editedAccount || account;
  });

  const achAccountsById = new Map<string, DisplayAchAccount>();
  mergedBaseAchAccounts.forEach((account) => {
    achAccountsById.set(account.id, account);
  });
  manualAchAccounts.forEach((account) => {
    achAccountsById.set(account.id, account);
  });

  const withAchDefaults = Array.from(achAccountsById.values()).map((account) => ({
    ...account,
    isDefault: achDefaultOverrideId ? account.id === achDefaultOverrideId : account.isDefault,
  }));

  const withCheckDefaults = [...mergedBaseCheckAccounts, ...manualCheckAccounts].map((account) => ({
    ...account,
    isDefault: checkDefaultOverrideId ? account.id === checkDefaultOverrideId : account.isDefault,
  }));

  const displayAchAccounts = withAchDefaults;
  const displayCheckAccounts = withCheckDefaults;
  const hasAchAccountAvailable = displayAchAccounts.length > 0;
  const hasCheckAddressAvailable = displayCheckAccounts.length > 0;
  const isAchAccountActionBlocked = (account: DisplayAchAccount) =>
    account.provisionStatus === 'Error' ||
    account.provisionStatus === 'CriticalError';

  const getAchDraftErrors = () => {
    const nickname = (achDraft.nickname || '').trim();
    const accountNumber = (achDraft.accountNumber || '').trim();
    const routingNumber = (achDraft.routingNumber || '').trim();
    const hasDuplicateNickname = displayAchAccounts.some(
      (item) =>
        (item.nickname || '').trim().toLowerCase() === nickname.toLowerCase() &&
        item.id !== editingAchId,
    );

    return {
      nickname: !nickname
        ? 'Nickname is required.'
        : hasDuplicateNickname
          ? 'ACH nickname already exists. Use a different nickname.'
          : '',
      accountNumber: !accountNumber
        ? 'Account number is required.'
        : !isValidAccountNumber(accountNumber)
          ? 'Invalid account number. Account number must be 4-17 digits and cannot be all zeros.'
          : '',
      routingNumber: !routingNumber
        ? 'Routing number is required.'
        : !isValidRoutingNumber(routingNumber)
          ? 'Invalid routing number. Please enter a valid 9-digit routing number.'
          : '',
    };
  };

  const achDraftErrors = getAchDraftErrors();
  const hasAchDraftErrors = Object.values(achDraftErrors).some(Boolean);
  const shouldShowAchDraftError =
    (field: keyof typeof achDraftTouched) => isAchDraftSubmitAttempted || achDraftTouched[field];

  const visibleAchAccounts = showAllAchAccounts
    ? displayAchAccounts
    : displayAchAccounts.slice(0, DEFAULT_VISIBLE_ACCOUNTS);
  const hiddenAchCount = Math.max(displayAchAccounts.length - DEFAULT_VISIBLE_ACCOUNTS, 0);

  const visibleCheckAccounts = showAllCheckAccounts
    ? displayCheckAccounts
    : displayCheckAccounts.slice(0, DEFAULT_VISIBLE_ACCOUNTS);
  const checkAccountsForCards = editingCheckId
    ? visibleCheckAccounts.filter((account) => account.id !== editingCheckId)
    : visibleCheckAccounts;
  const hiddenCheckCount = Math.max(displayCheckAccounts.length - DEFAULT_VISIBLE_ACCOUNTS, 0);

  const getCheckDraftErrors = () => {
    const nickname = checkDraft.nickname.trim();
    const address = checkDraft.address.trim();
    const city = checkDraft.city.trim();
    const state = checkDraft.state.trim();
    const zip = checkDraft.zip.trim();
    const checkMailingPhoneNumber = (checkDraft.mailingPhoneNumber || '').trim();
    const currentAccount = editingCheckId
      ? displayCheckAccounts.find((item) => item.id === editingCheckId)
      : null;

    const hasDuplicateNickname = displayCheckAccounts.some(
      (item) =>
        item.nickname.trim().toLowerCase() === nickname.toLowerCase() && item.id !== editingCheckId,
    );

    const isValidCheckMailingPhoneNumber =
      /^\+1[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(checkMailingPhoneNumber) ||
      /^\+1-[2-9]\d{2}-[2-9]\d{2}-\d{4}$/.test(checkMailingPhoneNumber);
    const isUnchangedLegacyCheckMailingPhoneNumber =
      !!editingCheckId &&
      normalizeUsPhoneNumber(checkMailingPhoneNumber) ===
      normalizeUsPhoneNumber(currentAccount?.mailingPhoneNumber || '');

    return {
      nickname: !nickname
        ? 'Nickname is required.'
        : hasDuplicateNickname
          ? 'Check nickname already exists. Use a different nickname.'
          : '',
      address: !address ? 'Address line 1 is required.' : '',
      city: !city ? 'City is required.' : '',
      state: !state ? 'State is required.' : '',
      zip: !zip ? 'Zip is required.' : '',
      mailingPhoneNumber: !checkMailingPhoneNumber
        ? 'Mobile number is required.'
        : !isValidCheckMailingPhoneNumber && !isUnchangedLegacyCheckMailingPhoneNumber
          ? 'Please enter a valid mobile number.'
          : '',
    };
  };

  const checkDraftErrors = getCheckDraftErrors();
  const hasCheckDraftErrors = Object.values(checkDraftErrors).some(Boolean);
  const shouldShowCheckDraftError =
    (field: keyof typeof checkDraftTouched) => isCheckDraftSubmitAttempted || checkDraftTouched[field];
  const isCheckDraftFormValid =
    !hasCheckDraftErrors && (checkDraftAddressVerified || checkDraftAddressKeptAsEntered);

  const resetAchDraftForm = () => {
    setEditingAchId(null);
    setAchDraft({ nickname: '', accountType: 'Checking', routingNumber: '', accountNumber: '' });
    setAchDraftTouched({ nickname: false, accountNumber: false, routingNumber: false });
    setIsAchDraftSubmitAttempted(false);
    setAchRoutingBankName(null);
    setAchRoutingLookupError(null);
    latestAchRoutingRef.current = '';
  };

  const validateAchRoutingNumberLookup = async (lookupKey: string): Promise<void> => {
    setAchRoutingLookupError(null);

    if (!isValidRoutingNumber(lookupKey)) {
      setAchRoutingBankName(null);
      return;
    }

    const cached = achRoutingLookupCacheRef.current[lookupKey];
    if (cached !== undefined) {
      setAchRoutingBankName(cached);
      setAchRoutingLookupError(
        achRoutingLookupFailedCacheRef.current[lookupKey]
          ? ROUTING_LOOKUP_UNAVAILABLE_MESSAGE
          : null,
      );
      return;
    }

    try {
      const bank = await getBankRoutingDetails(lookupKey);
      const resolvedBankName = bank.bankName?.trim() || null;
      achRoutingLookupCacheRef.current[lookupKey] = resolvedBankName;
      achRoutingLookupFailedCacheRef.current[lookupKey] = !resolvedBankName;

      if (latestAchRoutingRef.current === lookupKey) {
        setAchRoutingBankName(resolvedBankName);
        setAchRoutingLookupError(resolvedBankName ? null : ROUTING_LOOKUP_UNAVAILABLE_MESSAGE);
      }
    } catch {
      achRoutingLookupCacheRef.current[lookupKey] = null;
      achRoutingLookupFailedCacheRef.current[lookupKey] = true;

      if (latestAchRoutingRef.current === lookupKey) {
        setAchRoutingBankName(null);
        setAchRoutingLookupError(ROUTING_LOOKUP_UNAVAILABLE_MESSAGE);
      }
    }
  };

  const handleAchRoutingNumberChange = (rawValue: string) => {
    const nextValue = rawValue.replace(/\D/g, '').slice(0, 9);
    latestAchRoutingRef.current = nextValue;

    if (nextValue !== achDraft.routingNumber) {
      setAchRoutingBankName(null);
      setAchRoutingLookupError(null);
    }

    setAchDraft((prev) => ({
      ...prev,
      routingNumber: nextValue,
    }));
  };

  const handleAchRoutingNumberBlur = async (rawValue?: string) => {
    const lookupKey = (rawValue ?? achDraft.routingNumber ?? '').replace(/\D/g, '').slice(0, 9);
    await validateAchRoutingNumberLookup(lookupKey);
  };

  useEffect(() => {
    const lookupKey = (achDraft.routingNumber || '').replace(/\D/g, '').slice(0, 9);
    latestAchRoutingRef.current = lookupKey;

    if (isValidRoutingNumber(lookupKey)) {
      void validateAchRoutingNumberLookup(lookupKey);
    }
  }, [achDraft.routingNumber]);

  const refreshStatusTabDataFromServer = async () => {
    if (!accountId) return;

    setIsPaymentMethodsRefreshing(true);
    try {
      const response = await getVendorAccount(parseInt(accountId));
      const refreshedVendor = response.data;

      // Keep status view synced with server without resetting unsaved form inputs.
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          defaultPaymentMethod: refreshedVendor.defaultPaymentMethod,
          bank: refreshedVendor.bank,
          mailing: refreshedVendor.mailing,
          accounting: refreshedVendor.accounting,
          achAccounts: refreshedVendor.achAccounts || [],
          checkAccounts: refreshedVendor.checkAccounts || [],
          warningMessages: refreshedVendor.warningMessages || [],
        };
      });
      setAccountNumberStatus(refreshedVendor.bank?.hasAccountNumber ? '************' : '');
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message:
          axiosError.response?.data?.message ||
          'Failed to refresh Status tab data. Please try again.',
        type: 'warning',
      }));
    } finally {
      setIsPaymentMethodsRefreshing(false);
    }
  };

  const handleStartEditAchAccount = (account: DisplayAchAccount) => {
    if (isPaymentMethodsRefreshing) return;

    const editableRoutingNumber = (account.routingNumber || '').replace(/\D/g, '').slice(0, 9);
    const editableAccountNumber = (account.accountNumber || '').replace(/\D/g, '').slice(0, 17);

    setIsAddingAch(true);
    setEditingAchId(account.id);
    setAchDraftTouched({ nickname: false, accountNumber: false, routingNumber: false });
    setIsAchDraftSubmitAttempted(false);
    setAchDraft({
      nickname: account.nickname,
      accountType: account.accountType,
      routingNumber: editableRoutingNumber,
      accountNumber: editableAccountNumber,
    });
  };

  const handleAddAchAccount = async () => {
    if (isPaymentMethodsRefreshing) return;

    setIsAchDraftSubmitAttempted(true);
    const errors = getAchDraftErrors();
    if (Object.values(errors).some(Boolean)) {
      return;
    }

    const nickname = (achDraft.nickname || '').trim();
    const accountNumber = (achDraft.accountNumber || '').trim();
    const routingNumber = (achDraft.routingNumber || '').trim();

    const currentAccount = editingAchId
      ? displayAchAccounts.find((item) => item.id === editingAchId)
      : null;
    const shouldBeDefault = editingAchId
      ? !!currentAccount?.isDefault || !displayAchAccounts.some((item) => item.isDefault)
      : !displayAchAccounts.some((item) => item.isDefault);

    const payload = {
      nickname,
      accountType: achDraft.accountType,
      routingNumber,
      accountNumber,
      isDefault: shouldBeDefault,
      participationId: accountId || '',
    };

    setIsAddingAch(true);
    setIsSavingAchAccount(true);
    let isAchMutationSaved = false;
    try {
      if (editingAchId) {
        const isManualAccount = manualAchAccounts.some((item) => item.id === editingAchId);
        if (isManualAccount) {
          await addACHAccount(Number(accountId), payload);
          isAchMutationSaved = true;
        } else {
          await editACHAccount(Number(accountId), editingAchId, payload);
          isAchMutationSaved = true;
        }
      } else {
        await addACHAccount(Number(accountId), payload);
        isAchMutationSaved = true;
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      const errorMessage =
        axiosError.response?.data?.message || 'Failed to save ACH account.';
      setAlert((prev) => ({
        ...prev,
        message: errorMessage,
        type: 'error',
      }));
      return;
    } finally {
      setIsSavingAchAccount(false);
    }

    if (isAchMutationSaved) {
      setIsAddingAch(false);
      setAlert((prev) => ({
        ...prev,
        message: editingAchId ? 'ACH account updated successfully.' : 'ACH account added successfully.',
        type: 'success',
      }));
      setRefreshKey((prev) => prev + 1);
    }

    setShowAllAchAccounts(true);
    resetAchDraftForm();
  };

  const handleDeleteAchAccount = async (accountIdToDelete: string) => {
    if (isPaymentMethodsRefreshing || !accountId) return;

    const isManualAccount = manualAchAccounts.some((item) => item.id === accountIdToDelete);
    if (isManualAccount) {
      const deletedAccount = displayAchAccounts.find((item) => item.id === accountIdToDelete);
      const nextDisplayAccounts = displayAchAccounts.filter((item) => item.id !== accountIdToDelete);

      setManualAchAccounts((prev) => prev.filter((item) => item.id !== accountIdToDelete));
      setEditedAchAccounts((prev) => prev.filter((item) => item.id !== accountIdToDelete));

      if (editingAchId === accountIdToDelete) {
        setIsAddingAch(false);
        resetAchDraftForm();
      }

      if (achDefaultOverrideId === accountIdToDelete || deletedAccount?.isDefault) {
        const existingDefault = nextDisplayAccounts.find((item) => item.isDefault);
        setAchDefaultOverrideId(existingDefault?.id || nextDisplayAccounts[0]?.id || null);
      }
      setAlert((prev) => ({
        ...prev,
        message: 'ACH account deleted successfully.',
        type: 'success',
      }));
      setRefreshKey((prev) => prev + 1);
      return;
    }

    setIsPaymentMethodsRefreshing(true);
    try {
      await deleteACHAccount(Number(accountId), accountIdToDelete);
      if (editingAchId === accountIdToDelete) {
        setIsAddingAch(false);
        resetAchDraftForm();
      }
      setAlert((prev) => ({
        ...prev,
        message: 'ACH account deleted successfully.',
        type: 'success',
      }));
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      const errorMessage =
        axiosError.response?.data?.message || 'Failed to delete ACH account.';
      setAlert((prev) => ({
        ...prev,
        message: errorMessage,
        type: 'error',
      }));
    } finally {
      setIsPaymentMethodsRefreshing(false);
    }
  };

  const handleDeleteCheckAccount = async (accountIdToDelete: string) => {
    if (isPaymentMethodsRefreshing || !accountId) return;

    const isManualAccount = manualCheckAccounts.some((item) => item.id === accountIdToDelete);
    if (isManualAccount) {
      const deletedAccount = displayCheckAccounts.find((item) => item.id === accountIdToDelete);
      const nextDisplayAccounts = displayCheckAccounts.filter((item) => item.id !== accountIdToDelete);

      setManualCheckAccounts((prev) => prev.filter((item) => item.id !== accountIdToDelete));
      setEditedCheckAccounts((prev) => prev.filter((item) => item.id !== accountIdToDelete));

      if (editingCheckId === accountIdToDelete) {
        setIsAddingCheck(false);
        resetCheckDraftForm();
      }

      if (checkDefaultOverrideId === accountIdToDelete || deletedAccount?.isDefault) {
        const existingDefault = nextDisplayAccounts.find((item) => item.isDefault);
        setCheckDefaultOverrideId(existingDefault?.id || nextDisplayAccounts[0]?.id || null);
      }
      setAlert((prev) => ({
        ...prev,
        message: 'Check address deleted successfully.',
        type: 'success',
      }));
      setRefreshKey((prev) => prev + 1);
      return;
    }

    setIsPaymentMethodsRefreshing(true);
    try {
      await deleteCheckAccount(Number(accountId), accountIdToDelete);
      if (editingCheckId === accountIdToDelete) {
        setIsAddingCheck(false);
        resetCheckDraftForm();
      }
      setAlert((prev) => ({
        ...prev,
        message: 'Check address deleted successfully.',
        type: 'success',
      }));
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      const errorMessage =
        axiosError.response?.data?.message || 'Failed to delete check address.';
      setAlert((prev) => ({
        ...prev,
        message: errorMessage,
        type: 'error',
      }));
    } finally {
      setIsPaymentMethodsRefreshing(false);
    }
  };

  const handleSetDefaultAchAccount = async (accountIdToDefault: string) => {
    if (isPaymentMethodsRefreshing || !accountId || !canEdit) return;

    const selectedAccount = displayAchAccounts.find((item) => item.id === accountIdToDefault);
    if (!selectedAccount || selectedAccount.isDefault) return;

    const isManualAccount = manualAchAccounts.some((item) => item.id === accountIdToDefault);
    if (isManualAccount) {
      setAchDefaultOverrideId(accountIdToDefault);
      setAlert((prev) => ({
        ...prev,
        message: 'Default ACH account updated successfully.',
        type: 'success',
      }));
      setRefreshKey((prev) => prev + 1);
      return;
    }

    setIsPaymentMethodsRefreshing(true);
    try {
      await editACHAccount(Number(accountId), accountIdToDefault, {
        nickname: selectedAccount.nickname,
        accountType: selectedAccount.accountType,
        routingNumber: selectedAccount.routingNumber,
        isDefault: true,
      });
      setAlert((prev) => ({
        ...prev,
        message: 'Default ACH account updated successfully.',
        type: 'success',
      }));
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      const errorMessage = axiosError.response?.data?.message || 'Failed to set default ACH account.';
      setAlert((prev) => ({
        ...prev,
        message: errorMessage,
        type: 'error',
      }));
    } finally {
      setIsPaymentMethodsRefreshing(false);
    }
  };

  const handleSetDefaultCheckAccount = async (accountIdToDefault: string) => {
    if (isPaymentMethodsRefreshing || !accountId || !canEdit) return;

    const selectedAccount = displayCheckAccounts.find((item) => item.id === accountIdToDefault);
    if (!selectedAccount || selectedAccount.isDefault) return;

    const isManualAccount = manualCheckAccounts.some((item) => item.id === accountIdToDefault);
    if (isManualAccount) {
      setCheckDefaultOverrideId(accountIdToDefault);
      setAlert((prev) => ({
        ...prev,
        message: 'Default check address updated successfully.',
        type: 'success',
      }));
      setRefreshKey((prev) => prev + 1);
      return;
    }

    setIsPaymentMethodsRefreshing(true);
    try {
      await editCheckAccount(Number(accountId), accountIdToDefault, {
        nickname: selectedAccount.nickname,
        payeeName: selectedAccount.payeeName,
        recipientName: selectedAccount.recipientName,
        address: selectedAccount.address,
        addressLine2: selectedAccount.addressLine2 || '',
        city: selectedAccount.city,
        state: selectedAccount.state,
        zip: selectedAccount.zip,
        country: selectedAccount.country || 'US',
        isDefault: true,
      });
      setAlert((prev) => ({
        ...prev,
        message: 'Default check address updated successfully.',
        type: 'success',
      }));
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      const errorMessage = axiosError.response?.data?.message || 'Failed to set default check address.';
      setAlert((prev) => ({
        ...prev,
        message: errorMessage,
        type: 'error',
      }));
    } finally {
      setIsPaymentMethodsRefreshing(false);
    }
  };

  const handlePersistDefaultPaymentMethod = async (method: DefaultPaymentMethod) => {
    if (isPaymentMethodsRefreshing || !accountId || !canEdit) return;

    if (method === 'ACH' && !hasAchAccountAvailable) {
      setAlert((prev) => ({
        ...prev,
        message: 'Add an ACH account before setting ACH as the default payment method.',
        type: 'warning',
      }));
      return;
    }

    if (method === 'CHECK' && !hasCheckAddressAvailable) {
      setAlert((prev) => ({
        ...prev,
        message: 'Add a check mailing address before setting CHECK as the default payment method.',
        type: 'warning',
      }));
      return;
    }

    const previousDefaultAchEnabled = defaultAchEnabled;
    const previousDefaultCheckEnabled = defaultCheckMailingEnabled;
    const previousDefaultPaymentMethod = selectedDefaultPaymentMethod;

    setSelectedDefaultPaymentMethod(method);
    setValue('defaultAchEnabled', method === 'ACH');
    setValue('defaultCheckMailingEnabled', method === 'CHECK');

    setIsPaymentMethodsRefreshing(true);
    try {
      await updateVendorAccount(Number(accountId), { defaultPaymentMethod: method });
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      const errorMessage =
        axiosError.response?.data?.message || 'Failed to set default payment method.';
      setAlert((prev) => ({
        ...prev,
        message: errorMessage,
        type: 'error',
      }));

      setSelectedDefaultPaymentMethod(previousDefaultPaymentMethod);
      setValue('defaultAchEnabled', previousDefaultAchEnabled);
      setValue('defaultCheckMailingEnabled', previousDefaultCheckEnabled);
    } finally {
      setIsPaymentMethodsRefreshing(false);
    }
  };

  const handleRequestDeleteAchAccount = (accountIdToDelete: string) => {
    if (isPaymentMethodsRefreshing || !canEdit) return;
    setPendingDeletePaymentMethod({ type: 'ach', id: accountIdToDelete });
    setPaymentMethodDeleteConfirmationOpen(true);
  };

  const handleRequestDeleteCheckAccount = (accountIdToDelete: string) => {
    if (isPaymentMethodsRefreshing || !canEdit) return;
    setPendingDeletePaymentMethod({ type: 'check', id: accountIdToDelete });
    setPaymentMethodDeleteConfirmationOpen(true);
  };

  const handleCancelDeletePaymentMethod = () => {
    setPaymentMethodDeleteConfirmationOpen(false);
    setPendingDeletePaymentMethod(null);
  };

  const handleConfirmDeletePaymentMethod = async () => {
    if (!pendingDeletePaymentMethod) return;

    const { type, id } = pendingDeletePaymentMethod;
    setPaymentMethodDeleteConfirmationOpen(false);
    setPendingDeletePaymentMethod(null);

    if (type === 'ach') {
      await handleDeleteAchAccount(id);
      return;
    }

    await handleDeleteCheckAccount(id);
  };

  const resetCheckDraftForm = () => {
    setCheckDraftAddressVerified(false);
    setCheckDraftAddressKeptAsEntered(false);
    setEditingCheckId(null);
    setCheckDraftAddressKey((prev) => prev + 1);
    setCheckDraft({
      nickname: '',
      payeeName: '',
      recipientName: '',
      address: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      mailingPhoneNumber: '',
    });
    setCheckDraftTouched({
      nickname: false,
      address: false,
      city: false,
      state: false,
      zip: false,
      mailingPhoneNumber: false,
    });
    setIsCheckDraftSubmitAttempted(false);
    setCheckDraftSameAsParticipationAddress(false);
    setIsSyncingCheckDraftFromParticipation(false);
    setIsCheckDraftPayeeManuallyEdited(false);
    setIsCheckDraftRecipientManuallyEdited(false);
  };

  const isCheckDraftAddressSameAsVendorAddress = (addressValues: {
    address: string;
    addressLine2: string;
    city: string;
    state: string;
    zip: string;
  }) => {
    return (
      (addressValues.address || '').trim() === (participationAddress1 || '').trim() &&
      (addressValues.addressLine2 || '').trim() === (participationAddress2 || '').trim() &&
      (addressValues.city || '').trim() === (participationCity || '').trim() &&
      (addressValues.state || '').trim().toUpperCase() ===
      (participationState || '').trim().toUpperCase() &&
      (addressValues.zip || '').trim() === (participationZip || '').trim()
    );
  };

  const handleCheckDraftAddressSelect = async (addressOption: AddressPrediction): Promise<boolean> => {
    if (!addressOption) {
      const nextAddressValues = {
        address: '',
        addressLine2: checkDraft.addressLine2,
        city: checkDraft.city,
        state: checkDraft.state,
        zip: checkDraft.zip,
      };

      if (
        !isSyncingCheckDraftFromParticipation &&
        checkDraftSameAsParticipationAddress &&
        !isCheckDraftAddressSameAsVendorAddress(nextAddressValues)
      ) {
        setCheckDraftSameAsParticipationAddress(false);
      }

      setCheckDraftAddressVerified(false);
      setCheckDraftAddressKeptAsEntered(false);
      setCheckDraft((prev) => ({
        ...prev,
        address: '',
      }));
      return false;
    }

    const addressText = addressOption?.description || '';
    const addressLabel = (addressOption as unknown as { label?: string })?.label || '';
    const nextAddress = addressText || addressLabel;
    const nextAddressValues = {
      address: nextAddress,
      addressLine2: checkDraft.addressLine2,
      city: checkDraft.city,
      state: checkDraft.state,
      zip: checkDraft.zip,
    };

    if (
      !isSyncingCheckDraftFromParticipation &&
      checkDraftSameAsParticipationAddress &&
      !isCheckDraftAddressSameAsVendorAddress(nextAddressValues)
    ) {
      setCheckDraftSameAsParticipationAddress(false);
    }

    setCheckDraftAddressVerified(false);
    setCheckDraftAddressKeptAsEntered(false);
    setCheckDraftTouched((prev) => ({ ...prev, address: true }));
    setCheckDraft((prev) => ({
      ...prev,
      address: nextAddress,
    }));

    if (!addressOption?.placeId || !nextAddress) {
      return false;
    }

    setIsCheckDraftAddressVerifying(true);
    try {
      const validatedAddress = await validateAddress(nextAddress, addressOption.placeId);
      const {
        addressLine1,
        addressLine2,
        city: validatedCity,
        state: validatedState,
        zip: validatedZip,
      } = validatedAddress.data;

      setCheckDraft((prev) => ({
        ...prev,
        address: addressLine1 || prev.address,
        addressLine2: addressLine2 || prev.addressLine2,
        city: validatedCity || prev.city,
        state: validatedState || prev.state,
        zip: validatedZip || prev.zip,
      }));
      setCheckDraftAddressVerified(true);
      setCheckDraftAddressKeptAsEntered(false);
      return true;
    } catch (error) {
      setCheckDraftAddressVerified(false);
      setCheckDraftAddressKeptAsEntered(false);
      return false;
    } finally {
      setIsCheckDraftAddressVerifying(false);
    }
  };

  const handleStartEditCheckAddress = (account: DisplayCheckAccount) => {
    if (isPaymentMethodsRefreshing) return;

    const fallbackPayeeName = (account.payeeName || '').trim() || vendorName || data?.name || '';
    const fallbackRecipientName =
      (account.recipientName || '').trim() ||
      vendorName ||
      data?.name ||
      '';

    const draftAddressValues = {
      address: account.address,
      addressLine2: account.addressLine2 || '',
      city: account.city,
      state: account.state,
      zip: account.zip,
    };

    setIsAddingCheck(true);
    setEditingCheckId(account.id);
    setCheckDraftAddressVerified(true);
    setCheckDraftAddressKeptAsEntered(false);
    setCheckDraftTouched({
      nickname: false,
      address: false,
      city: false,
      state: false,
      zip: false,
      mailingPhoneNumber: false,
    });
    setIsCheckDraftSubmitAttempted(false);
    setCheckDraft({
      nickname: account.nickname,
      payeeName: fallbackPayeeName,
      recipientName: fallbackRecipientName,
      address: account.address,
      addressLine2: account.addressLine2 || '',
      city: account.city,
      state: account.state,
      zip: account.zip,
      mailingPhoneNumber: formatUsPhoneInput(account.mailingPhoneNumber || ''),
    });
    setCheckDraftSameAsParticipationAddress(
      isCheckDraftAddressSameAsVendorAddress(draftAddressValues),
    );
    setIsCheckDraftPayeeManuallyEdited(true);
    setIsCheckDraftRecipientManuallyEdited(true);
  };

  const handleAddCheckAddress = async () => {
    if (isPaymentMethodsRefreshing || !accountId) return;

    setIsCheckDraftSubmitAttempted(true);

    const nickname = checkDraft.nickname.trim();
    const payeeName = checkDraft.payeeName.trim();
    const recipientName = checkDraft.recipientName.trim();
    const address = checkDraft.address.trim();
    const addressLine2 = checkDraft.addressLine2.trim();
    const city = checkDraft.city.trim();
    const state = checkDraft.state.trim();
    const zip = checkDraft.zip.trim();
    const checkMailingPhoneNumber = (checkDraft.mailingPhoneNumber || '').trim();
    const currentAccount = editingCheckId
      ? displayCheckAccounts.find((item) => item.id === editingCheckId)
      : null;

    if (hasCheckDraftErrors) {
      return;
    }

    if (!checkDraftAddressVerified && !checkDraftAddressKeptAsEntered) {
      setAlert((prev) => ({
        ...prev,
        message: editingCheckId
          ? 'Please verify the address before saving.'
          : 'Please verify the address before adding.',
        type: 'warning',
      }));
      return;
    }

    const shouldBeDefault = editingCheckId
      ? !!currentAccount?.isDefault || !displayCheckAccounts.some((item) => item.isDefault)
      : !displayCheckAccounts.some((item) => item.isDefault);

    const payload = {
      nickname,
      payeeName,
      recipientName,
      address,
      addressLine2,
      city,
      state: state.toUpperCase(),
      zip,
      country: 'US',
      isDefault: shouldBeDefault,
      mailingPhoneNumber: normalizeUsPhoneNumber(checkMailingPhoneNumber),
    };

    setIsSavingCheckAddress(true);
    let isCheckMutationSaved = false;
    try {
      if (editingCheckId) {
        const isManualAccount = manualCheckAccounts.some((item) => item.id === editingCheckId);
        if (isManualAccount) {
          await addCheckAccount(Number(accountId), payload);
          isCheckMutationSaved = true;
        } else {
          await editCheckAccount(Number(accountId), editingCheckId, payload);
          isCheckMutationSaved = true;
        }
      } else {
        await addCheckAccount(Number(accountId), payload);
        isCheckMutationSaved = true;
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      const errorMessage =
        axiosError.response?.data?.message || 'Failed to save check address.';
      setAlert((prev) => ({
        ...prev,
        message: errorMessage,
        type: 'error',
      }));
      return;
    } finally {
      setIsSavingCheckAddress(false);
    }

    if (isCheckMutationSaved) {
      setAlert((prev) => ({
        ...prev,
        message: editingCheckId
          ? 'Check address updated successfully.'
          : 'Check address added successfully.',
        type: 'success',
      }));
      setRefreshKey((prev) => prev + 1);
    }

    setShowAllCheckAccounts(true);
    setIsAddingCheck(false);
    resetCheckDraftForm();
  };

  const handleVerifyCheckDraftAddress = async () => {
    const address = checkDraft.address.trim();
    const city = checkDraft.city.trim();
    const state = checkDraft.state.trim();
    const zip = checkDraft.zip.trim();

    if (!address || !city || !state || !zip) {
      setAlert((prev) => ({
        ...prev,
        message: 'Address line 1, city, state, and zip are required to verify.',
        type: 'warning',
      }));
      return;
    }

    setAddressVerificationType('checkDraft');
    setIsManualVerification(true);
    setVendorConfirmationOpen(true);
  };

  // Account Number visibility flags (used in ACH Accordion)
  const [, setIsAccountNumberVisible] = useState<boolean>(false);

  // Store the real account number from getAchAccountNumber for change detection
  const [realAccountNumber, setRealAccountNumber] = useState<string>('');
  const [isAccountNumberFetched, setIsAccountNumberFetched] = useState<boolean>(false);
  const [isStatusAccountNumberFetched, setIsStatusAccountNumberFetched] = useState<boolean>(false);

  // Account Number Status visibility flags (used in Status Tab)
  const [accountNumberStatus, setAccountNumberStatus] = useState<string>('');
  const [isAccountNumberStatusVisible, setIsAccountNumberStatusVisible] = useState<boolean>(false);

  // Track if isVendorConfirmationRequired was initially true (from API)
  const [isVendorConfirmationRequiredInitiallyTrue, setIsVendorConfirmationRequiredInitiallyTrue] =
    useState<boolean>(false);
  const [isAdminConfirmationRequired, setIsAdminConfirmationRequired] = useState<boolean>(false);
  const [isAdminConfirmationClicked, setIsAdminConfirmationClicked] = useState<boolean>(false);

  const [isResendMailActive, setIsResendMailActive] = useState<boolean>(false);
  const [isResendMailClicked, setIsResendMailClicked] = useState<boolean>(false);

  // NetSuite vendor sync state
  const [findNetSuiteVendorPopupOpen, setFindNetSuiteVendorPopupOpen] = useState<boolean>(false);

  const [unlinkConfirmationOpen, setUnlinkConfirmationOpen] = useState<boolean>(false);
  const [syncAction, setSyncAction] = useState<'sync' | 'relink'>('sync');



  // Track current baseline (updated after each verification) for detecting subsequent changes
  const [currentParticipationBaseline, setCurrentParticipationBaseline] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    isVerifiedByPopup: false,
    isKeptAsEntered: false,
    isVerifiedFromAutocomplete: false,
  });

  const [currentMailingBaseline, setCurrentMailingBaseline] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    isVerifiedByPopup: false,
    isKeptAsEntered: false,
    isVerifiedFromAutocomplete: false,
  });

  // Track if participation address has been modified from current baseline
  const [participationAddressChanged, setParticipationAddressChanged] = useState(false);

  // Track if mailing address has been modified from current baseline
  const [mailingAddressChanged, setMailingAddressChanged] = useState(false);

  // Accordion expansion states (independent of default method selection)
  const [achAccordionExpanded, setAchAccordionExpanded] = useState(false);
  const [checkAccordionExpanded, setCheckAccordionExpanded] = useState(false);
  const [selectedDefaultPaymentMethod, setSelectedDefaultPaymentMethod] =
    useState<DefaultPaymentMethod>('V');

  // Track whether addresses were selected from autocomplete vs manually typed
  const [participationAddressFromAutocomplete, setParticipationAddressFromAutocomplete] =
    useState(false);
  const [mailingAddressFromAutocomplete, setMailingAddressFromAutocomplete] = useState(false);

  // Track addresses verified through popup (to prevent blur from resetting verification)
  const [participationAddressVerifiedByPopup, setParticipationAddressVerifiedByPopup] =
    useState(false);
  const [mailingAddressVerifiedByPopup, setMailingAddressVerifiedByPopup] = useState(false);

  // Track addresses marked as "keep as entered" (custom addresses)
  const [participationAddressKeptAsEntered, setParticipationAddressKeptAsEntered] = useState(false);
  const [mailingAddressKeptAsEntered, setMailingAddressKeptAsEntered] = useState(false);

  // Track the original addresses that were "kept as entered" for comparison
  const [originalParticipationKeptAddress, setOriginalParticipationKeptAddress] = useState('');
  const [originalMailingKeptAddress, setOriginalMailingKeptAddress] = useState('');

  // Store original verification states from API to restore when reverting to original address
  const [originalParticipationVerificationState, setOriginalParticipationVerificationState] =
    useState({
      isVerified: false,
      isKeptAsEntered: false,
      keptAsEnteredAddress: '',
    });
  const [originalMailingVerificationState, setOriginalMailingVerificationState] = useState({
    isVerified: false,
    isKeptAsEntered: false,
    keptAsEnteredAddress: '',
  });

  // Store original address values from API (separate from baseline which can change after verification)
  const [originalParticipationAddress, setOriginalParticipationAddress] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [originalMailingAddress, setOriginalMailingAddress] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });

  const isParticipationAddressNeedsVerification =
    participationAddressChanged &&
    !participationAddressVerifiedByPopup &&
    !participationAddressFromAutocomplete &&
    !participationAddressKeptAsEntered;
  const isMailingAddressNeedsVerification =
    mailingAddressChanged &&
    !mailingAddressVerifiedByPopup &&
    !mailingAddressFromAutocomplete &&
    !mailingAddressKeptAsEntered;

  // Key to force re-render of address fields when checkbox is toggled
  const [, setMailingAddressKey] = useState(0);
  const [participationAddressKey, setParticipationAddressKey] = useState(0);

  // Track which address verification was triggered
  const [addressVerificationType, setAddressVerificationType] = useState<
    'participation' | 'mailing' | 'checkDraft'
  >('participation');
  const [isManualVerification, setIsManualVerification] = useState(false);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(EditVendorAccountFormSchema),
    defaultValues: VENDOR_ACCOUNT_EDIT_DEFAULT_VALUES,
  });

  const {
    formState: { isValid },
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    resetField,
    trigger,
    setError,
  } = formMethods;

  // Track if there are any actual changes (payload is not empty)
  const [hasChanges, setHasChanges] = useState(false);
  const [loadedFormSnapshot, setLoadedFormSnapshot] =
    useState<VendorAccountSubmitFormProps | null>(null);

  // Helper to treat null and empty string as equal for comparison
  const norm = (v: unknown) => (v === null || v === undefined ? '' : v);
  const isMasked = (val: unknown) => typeof val === 'string' && /^\*+$/.test(val);
  const hasChangedAccountNumber = (realVal: unknown, formVal: unknown) => {
    // Compare form value with real account number from getAchAccountNumber
    const formMasked = isMasked(formVal);
    const realEmpty = norm(realVal) === '';
    const formEmpty = norm(formVal) === '';
    if (realEmpty && (formMasked || formEmpty)) return false;
    if (formMasked) return false;
    return norm(realVal) !== norm(formVal);
  };
  const hasChanged = (a: unknown, b: unknown) => norm(a) !== norm(b);

  const buildVendorUpdatePayload = (
    values?: Partial<VendorAccountSubmitFormProps> | null,
    baseline?: Partial<VendorAccountSubmitFormProps> | null,
  ): Partial<VendorAccountSubmitFormProps> => {
    const sourceValues = values || {};
    const sourceBaseline = baseline || {};

    return {
      ...(hasChanged(sourceBaseline.name, sourceValues.name) && { name: sourceValues.name }),
      ...(hasChanged(sourceBaseline.vendorType, sourceValues.vendorType) && {
        vendorType: sourceValues.vendorType,
      }),
      ...(hasChanged(sourceBaseline.tin, sourceValues.tin)
        ? { tin: sourceValues.tin || null }
        : {}),
      ...(hasChanged(sourceBaseline.email, sourceValues.email) && {
        email: sourceValues.email,
      }),
      ...(hasChanged(
        normalizeUsPhoneNumber(sourceBaseline.mailingPhoneNumber),
        normalizeUsPhoneNumber(sourceValues.mailingPhoneNumber),
      ) && {
        mailingPhoneNumber: normalizeUsPhoneNumber(sourceValues.mailingPhoneNumber),
      }),
      ...(hasChanged(
        sourceBaseline.isVendorConfirmationRequired,
        sourceValues.isVendorConfirmationRequired,
      ) && {
        isVendorConfirmationRequired: sourceValues.isVendorConfirmationRequired,
      }),
      ...(hasChanged(sourceBaseline.accountType, sourceValues.accountType) && {
        accountType: sourceValues.accountType,
      }),
      ...(hasChangedAccountNumber(realAccountNumber, sourceValues.accountNumber) && {
        accountNumber: sourceValues.accountNumber,
      }),
      ...(hasChanged(sourceBaseline.routingNumber, sourceValues.routingNumber) && {
        routingNumber: sourceValues.routingNumber,
      }),
      ...(hasChanged(sourceBaseline.mailingAddress1, sourceValues.mailingAddress1) && {
        mailingAddress1: sourceValues.mailingAddress1,
      }),
      ...(hasChanged(sourceBaseline.mailingAddress2, sourceValues.mailingAddress2) && {
        mailingAddress2: sourceValues.mailingAddress2,
      }),
      ...(hasChanged(sourceBaseline.mailingCity, sourceValues.mailingCity) && {
        mailingCity: sourceValues.mailingCity,
      }),
      ...(hasChanged(sourceBaseline.mailingState, sourceValues.mailingState) && {
        mailingState: sourceValues.mailingState,
      }),
      ...(hasChanged(sourceBaseline.mailingZip, sourceValues.mailingZip) && {
        mailingZip: sourceValues.mailingZip,
      }),
      ...(hasChanged(
        sourceBaseline.participationAddress1,
        sourceValues.participationAddress1,
      ) && {
        participationAddress1: sourceValues.participationAddress1,
      }),
      ...(hasChanged(
        sourceBaseline.participationAddress2,
        sourceValues.participationAddress2,
      ) && {
        participationAddress2: sourceValues.participationAddress2,
      }),
      ...(hasChanged(sourceBaseline.participationCity, sourceValues.participationCity) && {
        participationCity: sourceValues.participationCity,
      }),
      ...(hasChanged(sourceBaseline.participationState, sourceValues.participationState) && {
        participationState: sourceValues.participationState,
      }),
      ...(hasChanged(sourceBaseline.participationZip, sourceValues.participationZip) && {
        participationZip: sourceValues.participationZip,
      }),
      ...(hasChanged(sourceBaseline.isAddressVerified, sourceValues.isAddressVerified) && {
        isAddressVerified: sourceValues.isAddressVerified,
      }),
      ...(hasChanged(
        sourceBaseline.isMailingAddressVerified,
        sourceValues.isMailingAddressVerified,
      ) && {
        isMailingAddressVerified: sourceValues.isMailingAddressVerified,
      }),
      ...(hasChanged(sourceBaseline.contactName, sourceValues.contactName) && {
        contactName: sourceValues.contactName,
      }),
      ...(sourceValues.notes?.trim() &&
        hasChanged(sourceBaseline.notes?.trim(), sourceValues.notes?.trim()) && {
        notes: sourceValues.notes?.trim() ?? '',
      }),
    };
  };

  // Use useWatch to subscribe to all form values
  const allValues = useWatch({ control: formMethods.control });

  useEffect(() => {
    if (!data || !loadedFormSnapshot) {
      setHasChanges(false);
      return;
    }
    const payload = buildVendorUpdatePayload(allValues, loadedFormSnapshot);
    setHasChanges(Object.keys(payload).length > 0);
  }, [data, allValues, loadedFormSnapshot, realAccountNumber]);

  const achEnabled = useWatch({
    control: formMethods.control,
    name: 'achEnabled',
    defaultValue: true,
  });
  const checkMailingEnabled = useWatch({
    control: formMethods.control,
    name: 'checkMailingEnabled',
    defaultValue: false,
  });
  const defaultAchEnabled = useWatch({
    control: formMethods.control,
    name: 'defaultAchEnabled',
    defaultValue: true,
  });
  const defaultCheckMailingEnabled = useWatch({
    control: formMethods.control,
    name: 'defaultCheckMailingEnabled',
    defaultValue: false,
  });
  const sameAsVendorAddress = useWatch({
    control: formMethods.control,
    name: 'sameAsVendorAddress',
    defaultValue: false,
  });

  const participationAddress1 = useWatch({
    control: formMethods.control,
    name: 'participationAddress1',
  });
  const vendorName = useWatch({ control: formMethods.control, name: 'name' });
  const participationAddress2 = useWatch({
    control: formMethods.control,
    name: 'participationAddress2',
  });
  const participationCity = useWatch({ control: formMethods.control, name: 'participationCity' });
  const participationState = useWatch({ control: formMethods.control, name: 'participationState' });
  const participationZip = useWatch({ control: formMethods.control, name: 'participationZip' });
  const hasParticipationAddress = [
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
  ].some((value) => (value || '').trim() !== '');
  // Only a complete vendor address can be verified
  const isParticipationAddressComplete = [
    participationAddress1,
    participationCity,
    participationState,
    participationZip,
  ].every((value) => (value || '').trim() !== '');

  const mailingAddress1 = useWatch({ control: formMethods.control, name: 'mailingAddress1' });
  const mailingAddress2 = useWatch({ control: formMethods.control, name: 'mailingAddress2' });
  const mailingCity = useWatch({ control: formMethods.control, name: 'mailingCity' });
  const mailingState = useWatch({ control: formMethods.control, name: 'mailingState' });
  const mailingZip = useWatch({ control: formMethods.control, name: 'mailingZip' });

  // Watch ACH fields to determine if user has entered any data
  const achType = useWatch({ control: formMethods.control, name: 'accountType' });
  const achAccountNumber = useWatch({ control: formMethods.control, name: 'accountNumber' });
  const achRoutingNumber = useWatch({ control: formMethods.control, name: 'routingNumber' });

  // Watch Check fields to determine if user has entered any data
  const mailingPhoneNumber = useWatch({ control: formMethods.control, name: 'mailingPhoneNumber' });

  const notesValue = useWatch({ control: formMethods.control, name: 'notes' }) || '';
  const [showCommentHistory, setShowCommentHistory] = useState(false);

  // Helper function to generate asterisks matching account number length
  const getMaskedAccountNumber = () => {
    // return '*'.repeat(accountNum.length);
    return '*************';
  };

  const handleVerifyAddress = async () => {
    if (!participationAddress1) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please enter an address to verify.',
        type: 'warning',
      }));
      return;
    }
    setAddressVerificationType('participation');
    setIsManualVerification(true);
    setVendorConfirmationOpen(true);
  };

  useEffect(() => {
    if (selectedDefaultPaymentMethod === 'ACH') {
      setAchAccordionExpanded(true);
      setCheckAccordionExpanded(false);
      return;
    }

    if (selectedDefaultPaymentMethod === 'CHECK') {
      setAchAccordionExpanded(false);
      setCheckAccordionExpanded(true);
      return;
    }

    setAchAccordionExpanded(false);
    setCheckAccordionExpanded(false);
  }, [selectedDefaultPaymentMethod]);

  const [lastAutoCompleteAddressState, setLastAutoCompleteAddressState] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [lastMailingAutoCompleteAddressState, setLastMailingAutoCompleteAddressState] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });

  // Flags to temporarily disable change tracking during autocomplete updates
  const [isUpdatingFromAutocomplete, setIsUpdatingFromAutocomplete] = useState(false);
  const [isUpdatingMailingFromAutocomplete, setIsUpdatingMailingFromAutocomplete] = useState(false);

  const getAddressTextFromOption = (address?: AddressPrediction | null) => {
    if (!address) return '';
    const addressLabel = (address as unknown as { label?: string }).label || '';
    const addressText = address.description || addressLabel || '';
    // Keep the text as typed (don't strip trailing spaces mid-typing); whitespace-only counts as empty
    return addressText.trim() ? addressText : '';
  };

  const handleParticipationAddressSelect = async (address?: AddressPrediction | null) => {
    const addressText = getAddressTextFromOption(address);

    if (!addressText) {
      setValue('participationAddress1', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setParticipationAddressFromAutocomplete(false);
      setParticipationAddressVerifiedByPopup(false);
      setParticipationAddressKeptAsEntered(false);
      setOriginalParticipationKeptAddress('');
      setLastAutoCompleteAddressState({
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
      });
      return;
    }

    // Set the updating flag FIRST, before any setValue calls that might trigger useEffects
    if (address?.placeId) {
      setIsUpdatingFromAutocomplete(true);
    }

    if (addressText) {
      setValue('participationAddress1', addressText);
      if (address?.placeId) {
        setParticipationAddressFromAutocomplete(true);
      } else {
        setParticipationAddressFromAutocomplete(false);
      }

      if (address?.placeId) {
        try {
          const validatedAddress = await validateAddress(addressText, address?.placeId);

          if (validatedAddress.data) {
            const { addressLine1, addressLine2, city, state, zip } = validatedAddress.data;
            if (addressLine1) setValue('participationAddress1', addressLine1);
            else {
              // setValue('participationAddress1', '');
              resetField('participationAddress1', { defaultValue: '' });
              setParticipationAddressKey((prev) => prev + 1); // Force re-render to clear field
            }
            if (addressLine2) setValue('participationAddress2', addressLine2);
            else setValue('participationAddress2', '');
            if (city) setValue('participationCity', city);
            else setValue('participationCity', '');
            if (state) setValue('participationState', state);
            else setValue('participationState', '');
            if (zip) setValue('participationZip', zip);
            else setValue('participationZip', '');

            setLastAutoCompleteAddressState({
              address1: addressLine1 || '',
              address2: addressLine2 || '',
              city: city || '',
              state: state || '',
              zip: zip || '',
            });
            setCurrentParticipationBaseline({
              address1: addressLine1 || '',
              address2: addressLine2 || '',
              city: city || '',
              state: state || '',
              zip: zip || '',
              isVerifiedByPopup: false,
              isKeptAsEntered: false,
              isVerifiedFromAutocomplete: true,
            });

            clearErrors([
              'participationAddress1',
              'participationAddress2',
              'participationCity',
              'participationState',
              'participationZip',
            ]);

            trigger([
              'participationAddress1',
              'participationAddress2',
              'participationCity',
              'participationState',
              'participationZip',
            ]);
          }
          // Re-enable change tracking after a brief delay
          setTimeout(() => setIsUpdatingFromAutocomplete(false), 500);
        } catch (error) {
          setValue('participationAddress1', addressText);
          setCurrentParticipationBaseline({
            address1: addressText || '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            isVerifiedByPopup: false,
            isKeptAsEntered: false,
            isVerifiedFromAutocomplete: true,
          });
          setIsUpdatingFromAutocomplete(false);
        }
      }
    }
  };

  const handleMailingAddressSelect = async (address?: AddressPrediction | null) => {
    const addressText = getAddressTextFromOption(address);

    if (!addressText) {
      setValue('mailingAddress1', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setMailingAddressFromAutocomplete(false);
      setMailingAddressVerifiedByPopup(false);
      setMailingAddressKeptAsEntered(false);
      setOriginalMailingKeptAddress('');
      setLastMailingAutoCompleteAddressState({
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
      });
      return;
    }

    // Set the updating flag FIRST, before any setValue calls that might trigger useEffects
    if (address?.placeId) {
      setIsUpdatingMailingFromAutocomplete(true);
    }

    if (addressText) {
      setValue('mailingAddress1', addressText);
      if (address?.placeId) {
        setMailingAddressFromAutocomplete(true);
      } else {
        setMailingAddressFromAutocomplete(false);
      }

      if (address?.placeId) {
        try {
          const validatedAddress = await validateAddress(addressText, address?.placeId);

          if (validatedAddress.data) {
            const { addressLine1, addressLine2, city, state, zip } = validatedAddress.data;
            if (addressLine1) setValue('mailingAddress1', addressLine1);
            else {
              // setValue('mailingAddress1', '');
              resetField('mailingAddress1', { defaultValue: '' });
              setMailingAddressKey((prev) => prev + 1); // Force re-render to clear field
            }
            if (addressLine2) setValue('mailingAddress2', addressLine2);
            else setValue('mailingAddress2', '');
            if (city) setValue('mailingCity', city);
            else setValue('mailingCity', '');
            if (state) setValue('mailingState', state);
            else setValue('mailingState', '');
            if (zip) setValue('mailingZip', zip);
            else setValue('mailingZip', '');

            setCurrentMailingBaseline({
              address1: addressLine1 || '',
              address2: addressLine2 || '',
              city: city || '',
              state: state || '',
              zip: zip || '',
              isVerifiedByPopup: false,
              isKeptAsEntered: false,
              isVerifiedFromAutocomplete: true,
            });

            setLastMailingAutoCompleteAddressState({
              address1: addressLine1 || '',
              address2: addressLine2 || '',
              city: city || '',
              state: state || '',
              zip: zip || '',
            });

            clearErrors([
              'mailingAddress1',
              'mailingAddress2',
              'mailingCity',
              'mailingState',
              'mailingZip',
            ]);

            trigger([
              'mailingAddress1',
              'mailingAddress2',
              'mailingCity',
              'mailingState',
              'mailingZip',
            ]);
          }
          // Re-enable change tracking after a brief delay
          setTimeout(() => setIsUpdatingMailingFromAutocomplete(false), 500);
        } catch (error) {
          setValue('mailingAddress1', addressText);
          setCurrentParticipationBaseline({
            address1: addressText || '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            isVerifiedByPopup: false,
            isKeptAsEntered: false,
            isVerifiedFromAutocomplete: true,
          });
          setIsUpdatingMailingFromAutocomplete(false);
        }
      }
    }
  };

  const handleVendorConfirmation = (selectedVendor?: VendorOption, keepAsEntered?: boolean) => {
    setVendorConfirmationOpen(false);

    // Handle "keep as entered" flag from popup
    if (keepAsEntered) {
      if (addressVerificationType === 'participation') {
        setParticipationAddressKeptAsEntered(true);
        setCurrentParticipationBaseline({
          address1: participationAddress1 || '',
          address2: participationAddress2 || '',
          city: participationCity || '',
          state: participationState || '',
          zip: participationZip || '',
          isVerifiedByPopup: false,
          isKeptAsEntered: true,
          isVerifiedFromAutocomplete: false,
        });
        setParticipationAddressVerifiedByPopup(false);
        // Store the current address as the original "kept as entered" address
        setOriginalParticipationKeptAddress(
          `${participationAddress1},${participationAddress2}, ${participationCity}, ${participationState} ${participationZip}`,
        );
      } else if (addressVerificationType === 'mailing') {
        setMailingAddressKeptAsEntered(true);
        setCurrentMailingBaseline({
          address1: mailingAddress1 || '',
          address2: mailingAddress2 || '',
          city: mailingCity || '',
          state: mailingState || '',
          zip: mailingZip || '',
          isVerifiedByPopup: false,
          isKeptAsEntered: true,
          isVerifiedFromAutocomplete: false,
        });
        setMailingAddressVerifiedByPopup(false);
        // Store the current address as the original "kept as entered" address
        setOriginalMailingKeptAddress(
          `${mailingAddress1},${mailingAddress2}, ${mailingCity}, ${mailingState} ${mailingZip}`,
        );
      } else if (addressVerificationType === 'checkDraft') {
        setCheckDraftAddressVerified(false);
        setCheckDraftAddressKeptAsEntered(true);
        setAlert((prev) => ({
          ...prev,
          message: 'Vendor address not verified by USPS.',
          type: 'warning',
        }));
      }
    }

    // Only proceed with form submission if this was triggered during form submission
    if (!isManualVerification) {
      const submitEvent = new CustomEvent('proceedWithSubmission');
      window.dispatchEvent(submitEvent);
    }
  };

  const handleVendorOptionSelect = async (vendor: VendorOption) => {
    // Create an address object similar to what AddressAutocomplete provides
    const addressForValidation: AddressPrediction = {
      description: vendor.address,
      placeId: vendor.placeId,
      structuredFormatting: {
        mainText: vendor.address,
        secondaryText: '',
      },
    };

    // Handle the address selection based on verification type
    if (addressVerificationType === 'participation') {
      setParticipationAddressVerifiedByPopup(true);
      setParticipationAddressKeptAsEntered(false);
      await handleParticipationAddressSelect(addressForValidation);
    } else if (addressVerificationType === 'mailing') {
      setMailingAddressVerifiedByPopup(true);
      setMailingAddressKeptAsEntered(false);
      await handleMailingAddressSelect(addressForValidation);
    } else if (addressVerificationType === 'checkDraft') {
      setIsCheckDraftAddressVerifying(true);
      const isVerified = await handleCheckDraftAddressSelect(addressForValidation);
      setCheckDraftAddressKeptAsEntered(!isVerified);
      setAlert((prev) => ({
        ...prev,
        message: isVerified
          ? 'Address verified successfully.'
          : 'Vendor address not verified by USPS.',
        type: isVerified ? 'success' : 'warning',
      }));
    }
  };

  // Update participation address baseline when verified (from autocomplete, popup, or kept as entered)
  // useEffect(() => {
  //   if (participationAddressVerifiedByPopup || participationAddressFromAutocomplete || participationAddressKeptAsEntered) {
  //     // Update baseline to current address values after verification
  //     setCurrentParticipationBaseline({
  //       address1: participationAddress1 || '',
  //       address2: participationAddress2 || '',
  //       city: participationCity || '',
  //       state: participationState || '',
  //       zip: participationZip || '',
  //       isVerifiedByPopup: false,
  //       isKeptAsEntered: false,
  //       isVerifiedFromAutocomplete: false,
  //     });
  //     // Reset change tracking since we've established a new baseline
  //     setParticipationAddressChanged(false);
  //   }
  // }, [participationAddressVerifiedByPopup, participationAddressFromAutocomplete, participationAddressKeptAsEntered, participationAddress1, participationAddress2, participationCity, participationState, participationZip]);

  // Update mailing address baseline when verified (from autocomplete, popup, or kept as entered)
  // useEffect(() => {
  //   if (mailingAddressVerifiedByPopup || mailingAddressFromAutocomplete || mailingAddressKeptAsEntered) {
  //     // Update baseline to current address values after verification
  //     setCurrentMailingBaseline({
  //       address1: mailingAddress1 || '',
  //       address2: mailingAddress2 || '',
  //       city: mailingCity || '',
  //       state: mailingState || '',
  //       zip: mailingZip || '',
  //       isVerifiedByPopup: mailingAddressVerifiedByPopup || false,
  //       isKeptAsEntered: mailingAddressKeptAsEntered || false,
  //       isVerifiedFromAutocomplete: false,
  //     });
  //     // Reset change tracking since we've established a new baseline
  //     setMailingAddressChanged(false);
  //   }
  // }, [mailingAddressVerifiedByPopup, mailingAddressFromAutocomplete, mailingAddressKeptAsEntered, mailingAddress1, mailingAddress2, mailingCity, mailingState, mailingZip]);

  // Track changes to any participation address fields after autocomplete selection
  useEffect(() => {
    // Skip change detection if we're currently updating from autocomplete
    if (isUpdatingFromAutocomplete) return;

    // Don't reset flags if we just set participationAddressFromAutocomplete to true
    // This prevents the race condition where the flag is set but useEffect runs before isUpdatingFromAutocomplete is properly set
    if (participationAddressFromAutocomplete && !lastAutoCompleteAddressState.address1) {
      return;
    }

    if (
      (participationAddressFromAutocomplete || participationAddressVerifiedByPopup) &&
      lastAutoCompleteAddressState.address1 &&
      (participationAddress1 !== lastAutoCompleteAddressState.address1 ||
        participationAddress2 !== lastAutoCompleteAddressState.address2 ||
        participationCity !== lastAutoCompleteAddressState.city ||
        participationState !== lastAutoCompleteAddressState.state ||
        participationZip !== lastAutoCompleteAddressState.zip)
    ) {
      // Any field has been manually changed after autocomplete selection
      setParticipationAddressFromAutocomplete(false);
      setParticipationAddressVerifiedByPopup(false);
      setParticipationAddressKeptAsEntered(false);
      setOriginalParticipationKeptAddress('');
      setLastAutoCompleteAddressState({
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
      });
    }
  }, [
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
    participationAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    lastAutoCompleteAddressState,
    isUpdatingFromAutocomplete,
  ]);

  // Track changes to any mailing address fields after autocomplete selection
  useEffect(() => {
    // Skip tracking if we're currently updating from autocomplete
    if (isUpdatingMailingFromAutocomplete) return;

    // Skip this tracking when "Same as Vendor Address" is checked
    if (sameAsVendorAddress) return;

    // Don't reset flags if we just set mailingAddressFromAutocomplete to true
    // This prevents the race condition where the flag is set but useEffect runs before isUpdatingFromAutocomplete is properly set
    if (mailingAddressFromAutocomplete && !lastMailingAutoCompleteAddressState.address1) {
      return;
    }

    if (
      (mailingAddressFromAutocomplete || mailingAddressVerifiedByPopup) &&
      lastMailingAutoCompleteAddressState.address1 &&
      (mailingAddress1 !== lastMailingAutoCompleteAddressState.address1 ||
        mailingAddress2 !== lastMailingAutoCompleteAddressState.address2 ||
        mailingCity !== lastMailingAutoCompleteAddressState.city ||
        mailingState !== lastMailingAutoCompleteAddressState.state ||
        mailingZip !== lastMailingAutoCompleteAddressState.zip)
    ) {
      // Any field has been manually changed after autocomplete selection
      setMailingAddressFromAutocomplete(false);
      setMailingAddressVerifiedByPopup(false);
      setMailingAddressKeptAsEntered(false);
      setOriginalMailingKeptAddress('');
      setLastMailingAutoCompleteAddressState({
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
      });
    }
  }, [
    mailingAddress1,
    mailingAddress2,
    mailingCity,
    mailingState,
    mailingZip,
    mailingAddressFromAutocomplete,
    mailingAddressVerifiedByPopup,
    lastMailingAutoCompleteAddressState,
    isUpdatingMailingFromAutocomplete,
    sameAsVendorAddress,
  ]);

  // Effect to manage achEnabled and checkMailingEnabled based on default selection and field content
  useEffect(() => {
    // Helper function to check if a field has meaningful content
    const hasContent = (value: unknown) => {
      if (!value) return false;
      if (typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        if (obj.id || obj.label) {
          const id = obj.id as string | undefined;
          const label = obj.label as string | undefined;
          return Boolean(id?.trim() || label?.trim());
        }
        return false;
      }
      return String(value).trim() !== '';
    };

    // Determine if ACH fields have any meaningful content
    const hasAchData =
      hasContent(achType) || hasContent(achAccountNumber) || hasContent(achRoutingNumber);

    // Determine if Check fields have any meaningful content
    const hasCheckData =
      hasContent(mailingPhoneNumber) ||
      hasContent(mailingAddress1) ||
      hasContent(mailingAddress2) ||
      hasContent(mailingCity) ||
      hasContent(mailingState) ||
      hasContent(mailingZip);

    // Set achEnabled: true if it's the default method OR if user has entered ACH data
    const shouldEnableAch = defaultAchEnabled || hasAchData;

    // Set checkMailingEnabled: true if it's the default method OR if user has entered Check data
    const shouldEnableCheckMailing = defaultCheckMailingEnabled || hasCheckData;

    // Only update if values have changed to avoid unnecessary re-renders
    if (achEnabled !== shouldEnableAch) {
      setValue('achEnabled', shouldEnableAch);
    }

    if (checkMailingEnabled !== shouldEnableCheckMailing) {
      setValue('checkMailingEnabled', shouldEnableCheckMailing);
    }
  }, [
    defaultAchEnabled,
    defaultCheckMailingEnabled,
    achType,
    achAccountNumber,
    achRoutingNumber,
    mailingPhoneNumber,
    mailingAddress1,
    mailingAddress2,
    mailingCity,
    mailingState,
    mailingZip,
    achEnabled,
    checkMailingEnabled,
    setValue,
  ]);

  // Detect changes to participation address and reset verification if modified
  useEffect(() => {
    // Skip change detection if we're currently updating from autocomplete
    if (isUpdatingFromAutocomplete) return;

    const isBackToOriginal =
      participationAddress1 === originalParticipationAddress.address1 &&
      participationAddress2 === originalParticipationAddress.address2 &&
      participationCity === originalParticipationAddress.city &&
      participationState === originalParticipationAddress.state &&
      participationZip === originalParticipationAddress.zip;

    const hasChangedFromBaseline =
      participationAddress1 !== currentParticipationBaseline.address1 ||
      participationAddress2 !== currentParticipationBaseline.address2 ||
      participationCity !== currentParticipationBaseline.city ||
      participationState !== currentParticipationBaseline.state ||
      participationZip !== currentParticipationBaseline.zip;

    // If back to original API address, restore original verification state
    if (isBackToOriginal) {
      setParticipationAddressChanged(false);
      if (originalParticipationVerificationState.isVerified) {
        setParticipationAddressVerifiedByPopup(true);
        setParticipationAddressFromAutocomplete(false);
        setParticipationAddressKeptAsEntered(false);
        setOriginalParticipationKeptAddress('');
      } else if (originalParticipationVerificationState.isKeptAsEntered) {
        setParticipationAddressKeptAsEntered(true);
        setParticipationAddressVerifiedByPopup(false);
        setParticipationAddressFromAutocomplete(false);
        setOriginalParticipationKeptAddress(
          originalParticipationVerificationState.keptAsEnteredAddress,
        );
      }
    } else if (hasChangedFromBaseline) {
      // Address has changed from baseline - reset verification and show verify button
      if (!participationAddressChanged) {
        setParticipationAddressChanged(true);
        setParticipationAddressFromAutocomplete(false);
        setParticipationAddressVerifiedByPopup(false);
        setParticipationAddressKeptAsEntered(false);
        setOriginalParticipationKeptAddress('');
      }
    } else {
      // Address matches baseline but may have been verified since - keep current state
      setParticipationAddressChanged(false);
      setParticipationAddressFromAutocomplete(
        currentParticipationBaseline.isVerifiedFromAutocomplete,
      );
      setParticipationAddressVerifiedByPopup(currentParticipationBaseline.isVerifiedByPopup);
      setParticipationAddressKeptAsEntered(currentParticipationBaseline.isKeptAsEntered);
    }
  }, [
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
    currentParticipationBaseline,
    originalParticipationAddress,
    participationAddressChanged,
    participationAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    participationAddressKeptAsEntered,
    isUpdatingFromAutocomplete,
    originalParticipationVerificationState,
  ]);

  // Detect changes to mailing address and reset verification if modified
  useEffect(() => {
    // Skip change detection if we're currently updating from autocomplete
    if (isUpdatingMailingFromAutocomplete) return;
    if (sameAsVendorAddress) return;

    const isBackToOriginal =
      mailingAddress1 === originalMailingAddress.address1 &&
      mailingAddress2 === originalMailingAddress.address2 &&
      mailingCity === originalMailingAddress.city &&
      mailingState === originalMailingAddress.state &&
      mailingZip === originalMailingAddress.zip;

    const hasChangedFromBaseline =
      mailingAddress1 !== currentMailingBaseline.address1 ||
      mailingAddress2 !== currentMailingBaseline.address2 ||
      mailingCity !== currentMailingBaseline.city ||
      mailingState !== currentMailingBaseline.state ||
      mailingZip !== currentMailingBaseline.zip;

    // If back to original API address, restore original verification state
    if (isBackToOriginal) {
      setMailingAddressChanged(false);
      if (originalMailingVerificationState.isVerified) {
        setMailingAddressVerifiedByPopup(true);
        setMailingAddressFromAutocomplete(false);
        setMailingAddressKeptAsEntered(false);
        setOriginalMailingKeptAddress('');
      } else if (originalMailingVerificationState.isKeptAsEntered) {
        setMailingAddressKeptAsEntered(true);
        setMailingAddressVerifiedByPopup(false);
        setMailingAddressFromAutocomplete(false);
        setOriginalMailingKeptAddress(originalMailingVerificationState.keptAsEnteredAddress);
      }
    } else if (hasChangedFromBaseline) {
      // Address has changed from baseline - reset verification and show verify button
      if (!mailingAddressChanged) {
        setMailingAddressChanged(true);
        setMailingAddressFromAutocomplete(false);
        setMailingAddressVerifiedByPopup(false);
        setMailingAddressKeptAsEntered(false);
        setOriginalMailingKeptAddress('');
      }
    } else {
      // Address matches baseline but may have been verified since - keep current state
      setMailingAddressChanged(false);
      setMailingAddressFromAutocomplete(currentMailingBaseline.isVerifiedFromAutocomplete);
      setMailingAddressVerifiedByPopup(currentMailingBaseline.isVerifiedByPopup);
      setMailingAddressKeptAsEntered(currentMailingBaseline.isKeptAsEntered);
    }
  }, [
    mailingAddress1,
    mailingAddress2,
    mailingCity,
    mailingState,
    mailingZip,
    currentMailingBaseline,
    originalMailingAddress,
    mailingAddressChanged,
    mailingAddressFromAutocomplete,
    mailingAddressVerifiedByPopup,
    mailingAddressKeptAsEntered,
    isUpdatingMailingFromAutocomplete,
    originalMailingVerificationState,
  ]);

  useEffect(() => {
    if (!achEnabled) {
      clearErrors(['accountType', 'accountNumber', 'routingNumber']);
    }
  }, [achEnabled, clearErrors]);

  useEffect(() => {
    if (!checkMailingEnabled) {
      clearErrors([
        'mailingAddress1',
        'mailingAddress2',
        'mailingCity',
        'mailingState',
        'mailingZip',
        'mailingPhoneNumber',
      ]);
    }
  }, [checkMailingEnabled, clearErrors]);

  // Handle "Same as Vendor Address" checkbox
  useEffect(() => {
    if (sameAsVendorAddress) {
      // Copy vendor address to mailing address
      setValue('mailingAddress1', participationAddress1 || '');
      setValue('mailingAddress2', participationAddress2 || '');
      setValue('mailingCity', participationCity || '');
      setValue('mailingState', participationState || '');
      setValue('mailingZip', participationZip || '');

      // Synchronize mailing verification flags and baseline
      setMailingAddressFromAutocomplete(participationAddressFromAutocomplete);
      setMailingAddressVerifiedByPopup(participationAddressVerifiedByPopup);
      setMailingAddressKeptAsEntered(participationAddressKeptAsEntered);
      if (participationAddressKeptAsEntered) {
        setOriginalMailingKeptAddress(
          `${participationAddress1}, ${participationAddress2}, ${participationCity}, ${participationState} ${participationZip}`,
        );
      } else {
        setOriginalMailingKeptAddress('');
      }
      // Set isMailingAddressVerified to match participation verification status
      const isVerified =
        participationAddressFromAutocomplete || participationAddressVerifiedByPopup;
      formMethods.setValue('isMailingAddressVerified', isVerified, { shouldValidate: true });

      // Also update lastMailingAutoCompleteAddressState if needed
      if (participationAddressFromAutocomplete) {
        setLastMailingAutoCompleteAddressState({
          address1: participationAddress1 || '',
          address2: participationAddress2 || '',
          city: participationCity || '',
          state: participationState || '',
          zip: participationZip || '',
        });
      } else {
        setLastMailingAutoCompleteAddressState({
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
        });
      }
    } else {
      setOriginalMailingKeptAddress('');
      setLastMailingAutoCompleteAddressState({
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
      });

      // Reset mailing baseline
      setCurrentMailingBaseline({
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        isVerifiedByPopup: false,
        isKeptAsEntered: false,
        isVerifiedFromAutocomplete: false,
      });

      // Force re-render by changing key only when unchecking
      setMailingAddressKey((prev) => prev + 1);
    }
  }, [
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
    sameAsVendorAddress,
    setValue,
    resetField,
  ]);

  // Handle "Same as Vendor Address" checkbox
  useEffect(() => {
    if (!sameAsVendorAddress) {
      // When unchecked, clear mailing address values and verification status
      resetField('mailingAddress1');
      resetField('mailingAddress2');
      resetField('mailingCity');
      resetField('mailingState');
      resetField('mailingZip');

      // Clear verification status
      setMailingAddressFromAutocomplete(originalMailingVerificationState.isVerified);
      setMailingAddressVerifiedByPopup(false);
      setMailingAddressKeptAsEntered(originalMailingVerificationState.isKeptAsEntered);
      setOriginalMailingKeptAddress('');
      setLastMailingAutoCompleteAddressState({
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
      });

      // Reset mailing baseline
      setCurrentMailingBaseline({
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        isVerifiedByPopup: false,
        isKeptAsEntered: false,
        isVerifiedFromAutocomplete: false,
      });

      // Force re-render by changing key only when unchecking
      setMailingAddressKey((prev) => prev + 1);
    }
  }, [sameAsVendorAddress, setValue, resetField]);

  const checkForCustomAddresses = () => {
    const hasCustomParticipationAddress =
      isParticipationAddressComplete &&
      !participationAddressFromAutocomplete &&
      !participationAddressVerifiedByPopup &&
      !participationAddressKeptAsEntered;
    const hasCustomMailingAddress =
      mailingAddress1 &&
      !mailingAddressFromAutocomplete &&
      !mailingAddressVerifiedByPopup &&
      !mailingAddressKeptAsEntered &&
      checkMailingEnabled;

    if (hasCustomParticipationAddress || hasCustomMailingAddress) {
      // Show vendor confirmation popup instead of address confirmation
      setIsManualVerification(false); // Mark as form submission verification
      setVendorConfirmationOpen(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    window.checkCustomAddresses = checkForCustomAddresses;
  }, [
    participationAddress1,
    mailingAddress1,
    participationAddressFromAutocomplete,
    mailingAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    mailingAddressVerifiedByPopup,
    participationAddressKeptAsEntered,
    mailingAddressKeptAsEntered,
    originalParticipationKeptAddress,
    originalMailingKeptAddress,
    checkMailingEnabled,
  ]);

  const onSubmit = async (submitData: VendorAccountSubmitFormProps) => {
    if (!data) {
      return;
    }
    setIsFormSubmitting(true);
    const payload = buildVendorUpdatePayload(submitData, loadedFormSnapshot);
    updateVendorAccount(Number(accountId), payload)
      .then((response) => {
        const warningMessages = response.data?.warningMessages;
        if (warningMessages && warningMessages.length > 0) {
          setAlert((prev) => ({
            ...prev,
            message: warningMessages,
            type: 'warning',
          }));
        } else {
          setAlert((prev) => ({
            ...prev,
            message: 'Vendor account updated successfully.',
            type: 'success',
          }));
        }
        navigate('/vendors');
      })
      .catch((error: AxiosError<ErrorData>) => {
        const message = error.response?.data?.message || 'Failed to update vendor account.';
        if (error.response?.status === 400 && message.toLowerCase().includes('tin')) {
          setError('tin', { message });
          setAlert((prev) => ({
            ...prev,
            message,
            type: 'error',
          }));
        } else {
          setAlert((prev) => ({
            ...prev,
            message,
            type: 'error',
          }));
        }
      })
      .finally(() => {
        setIsFormSubmitting(false);
      });
  };

  const onSubmitForm = (values: VendorAccountSubmitFormProps) => {
    if (!checkForCustomAddresses()) {
      setPendingSubmissionData(values);
      return;
    }

    onSubmit(values);
  };

  useEffect(() => {
    const handleProceedWithSubmission = () => {
      if (pendingSubmissionData) {
        onSubmit(pendingSubmissionData);
        setPendingSubmissionData(null);
      }
    };

    window.addEventListener('proceedWithSubmission', handleProceedWithSubmission);
    return () => {
      window.removeEventListener('proceedWithSubmission', handleProceedWithSubmission);
    };
  }, [pendingSubmissionData]);

  const onCancel = () => {
    reset();
    // After reset, re-fetch and set the actual account number if needed
    if (accountId && data?.bank?.hasAccountNumber) {
      getAchAccountNumber(Number(accountId))
        .then((res) => {
          setValue('accountNumber', res.data.accountNumber);
          setAccountNumberStatus(res.data.accountNumber);
          setRealAccountNumber(res.data.accountNumber || '');
        })
        .catch((error) => {
          let errorMessage = 'Error fetching account number.';
          const axiosError = error as AxiosError<ErrorData>;
          if (axiosError?.isAxiosError) {
            errorMessage = axiosError.response?.data?.message ?? errorMessage;
          }
          setAlert((prev) => ({
            ...prev,
            message: errorMessage,
            type: 'error',
          }));
        });
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Load account data for edit mode
  useEffect(() => {
    if (accountId) {
      setIsPageLoading(true);
      setLoadedFormSnapshot(null);
      getVendorAccount(parseInt(accountId))
        .then((response) => {
          setHistoryData(response.data);
          setData(response.data);
          const defaultAchAccount =
            response.data.achAccounts?.find((account) => account.isDefault) ||
            response.data.achAccounts?.[0];
          const defaultCheckAccount =
            response.data.checkAccounts?.find((account) => account.isDefault) ||
            response.data.checkAccounts?.[0];
          const provisioningStatus = response.data?.bank?.integration?.provisioning?.status;
          const isAchEditable =
            !response.data?.bank ||
            provisioningStatus === 'NotStarted' ||
            provisioningStatus === 'Error';
          // const isCheckEditable =
          //   !response.data?.mailing?.provisioning ||
          //   response.data?.mailing?.provisioning?.status === 'NotStarted' ||
          //   response.data?.mailing?.provisioning?.status === 'Error';

          if (!isAchEditable) {
            setIsVendorConfirmationRequiredInitiallyTrue(true);
          }
          if (response.data.vendorStatus === 'CONFIRMATION_REQUIRED_ADMIN') {
            setIsAdminConfirmationRequired(true);
          } else {
            setIsAdminConfirmationRequired(false);
          }
          if (
            (response.data.vendorStatus === 'CONFIRMATION_REQUIRED_VENDOR' ||
              response.data.vendorStatus === 'NEW') &&
            response.data.isVendorConfirmationRequired === true
          ) {
            setIsResendMailActive(true);
          } else {
            setIsResendMailActive(false);
          }

          // Store original address values from API
          const participationAddr = response.data.participationAddress || {};
          const mailingAddr = response.data.mailing || {};
          const mailingAddressFromCheck = {
            address1: defaultCheckAccount?.address || '',
            address2: defaultCheckAccount?.addressLine2 || '',
            city: defaultCheckAccount?.city || '',
            state: defaultCheckAccount?.state || '',
            zip: defaultCheckAccount?.zip || '',
          };
          setAccountNumberStatus(response.data.bank?.hasAccountNumber ? '************' : '');

          const participationAddressObj = {
            address1: participationAddr.address1 || '',
            address2: participationAddr.address2 || '',
            city: participationAddr.city || '',
            state: participationAddr.state || '',
            zip: participationAddr.zip || '',
          };

          const mailingAddressObj = {
            address1: mailingAddr.address1 || '',
            address2: mailingAddr.address2 || '',
            city: mailingAddr.city || '',
            state: mailingAddr.state || '',
            zip: mailingAddr.zip || '',
          };
          const initialMailingAddressObj = {
            address1: mailingAddressFromCheck.address1 || mailingAddressObj.address1,
            address2: mailingAddressFromCheck.address2 || mailingAddressObj.address2,
            city: mailingAddressFromCheck.city || mailingAddressObj.city,
            state: mailingAddressFromCheck.state || mailingAddressObj.state,
            zip: mailingAddressFromCheck.zip || mailingAddressObj.zip,
          };
          // Set current baseline to match original (will be updated when address is verified)
          setCurrentParticipationBaseline({
            address1: participationAddressObj.address1,
            address2: participationAddressObj.address2,
            city: participationAddressObj.city,
            state: participationAddressObj.state,
            zip: participationAddressObj.zip,
            isVerifiedByPopup: participationAddr.isAddressVerified || false,
            isKeptAsEntered: participationAddr.isKeptAsEntered || false,
            isVerifiedFromAutocomplete: false,
          });
          setCurrentMailingBaseline({
            address1: initialMailingAddressObj.address1,
            address2: initialMailingAddressObj.address2,
            city: initialMailingAddressObj.city,
            state: initialMailingAddressObj.state,
            zip: initialMailingAddressObj.zip,
            isVerifiedByPopup: mailingAddr.isAddressVerified || false,
            isKeptAsEntered: mailingAddr.isKeptAsEntered || false,
            isVerifiedFromAutocomplete: false,
          });

          // Store original address values from API
          setOriginalParticipationAddress(participationAddressObj);
          setOriginalMailingAddress(initialMailingAddressObj);

          // Initially, no changes from baseline
          setParticipationAddressChanged(false);
          setMailingAddressChanged(false);

          // Initialize verification flags from API data
          if (participationAddr.isAddressVerified === true) {
            // If address is verified from API, mark it as verified by popup (shows green checkmark)
            setParticipationAddressVerifiedByPopup(true);
            setParticipationAddressFromAutocomplete(false);
            setParticipationAddressKeptAsEntered(false);
            setOriginalParticipationKeptAddress('');
            // Store original verification state
            setOriginalParticipationVerificationState({
              isVerified: true,
              isKeptAsEntered: false,
              keptAsEnteredAddress: '',
            });
          } else if (participationAddr.address1) {
            // If not verified from API but has address, treat as a custom address (shows warning icon)
            setParticipationAddressKeptAsEntered(true);
            setParticipationAddressVerifiedByPopup(false);
            setParticipationAddressFromAutocomplete(false);
            const keptAddress = `${participationAddr.address1}, ${participationAddr.city}, ${participationAddr.state} ${participationAddr.zip}`;
            setOriginalParticipationKeptAddress(keptAddress);
            // Store original verification state
            setOriginalParticipationVerificationState({
              isVerified: false,
              isKeptAsEntered: true,
              keptAsEnteredAddress: keptAddress,
            });
          }

          if (mailingAddr.isAddressVerified === true) {
            // If address is verified from API, mark it as verified by popup (shows green checkmark)
            setMailingAddressVerifiedByPopup(true);
            setMailingAddressFromAutocomplete(false);
            setMailingAddressKeptAsEntered(false);
            setOriginalMailingKeptAddress('');
            // Store original verification state
            setOriginalMailingVerificationState({
              isVerified: true,
              isKeptAsEntered: false,
              keptAsEnteredAddress: '',
            });
          } else if (initialMailingAddressObj.address1) {
            // If not verified from API but has address, treat as a custom address (shows warning icon)
            setMailingAddressKeptAsEntered(true);
            setMailingAddressVerifiedByPopup(false);
            setMailingAddressFromAutocomplete(false);
            const keptAddress = `${initialMailingAddressObj.address1}, ${initialMailingAddressObj.city}, ${initialMailingAddressObj.state} ${initialMailingAddressObj.zip}`;
            setOriginalMailingKeptAddress(keptAddress);
            // Store original verification state
            setOriginalMailingVerificationState({
              isVerified: false,
              isKeptAsEntered: true,
              keptAsEnteredAddress: keptAddress,
            });
          }

          // Format phone number to match form expectations
          const phoneFromApi = response.data.mailing?.phoneNumber || '';
          // If phone is already in +1XXXXXXXXXX format, use it as-is
          // Otherwise, extract digits and format it
          const formattedPhone = phoneFromApi.match(/^\+1\d{10}$/)
            ? phoneFromApi
            : phoneFromApi
              ? `+1${phoneFromApi.replace(/\D/g, '').slice(-10)}`
              : '';

          const initialFormValues = {
            name: response.data.name,
            vendorType: response.data.vendorType,
            mailingPhoneNumber: formattedPhone,
            email: response.data.email,
            isVendorConfirmationRequired: response.data.isVendorConfirmationRequired,
            accountType: defaultAchAccount?.accountType || response.data.bank?.accountType || '',
            accountNumber: response.data.bank?.hasAccountNumber ? '************' : '',
            routingNumber: defaultAchAccount?.routingNumber || response.data.bank?.routingNumber || '',
            mailingAddress1: mailingAddressFromCheck.address1 || response.data.mailing?.address1 || '',
            mailingAddress2: mailingAddressFromCheck.address2 || response.data.mailing?.address2 || '',
            mailingCity: mailingAddressFromCheck.city || response.data.mailing?.city || '',
            mailingState: mailingAddressFromCheck.state || response.data.mailing?.state || '',
            mailingZip: mailingAddressFromCheck.zip || response.data.mailing?.zip || '',
            tin: response.data.tin ?? '',
            participationAddress1: participationAddressObj.address1,
            participationAddress2: participationAddressObj.address2,
            participationCity: participationAddressObj.city,
            participationState: participationAddressObj.state,
            participationZip: participationAddressObj.zip,
            // Set payment method flags based on existing data
            // ACH is enabled if: default is ACH OR if any ACH field is present (accountType or routingNumber)
            achEnabled:
              response.data.defaultPaymentMethod === 'ACH' ||
              !!(
                defaultAchAccount?.accountType ||
                defaultAchAccount?.routingNumber ||
                response.data.bank?.accountType ||
                response.data.bank?.routingNumber
              ),
            checkMailingEnabled:
              response.data.defaultPaymentMethod === 'CHECK' ||
              !!(
                defaultCheckAccount?.address ||
                defaultCheckAccount?.city ||
                response.data.mailing?.address1 ||
                response.data.mailing?.city
              ),
            defaultAchEnabled: response.data.defaultPaymentMethod === 'ACH',
            defaultCheckMailingEnabled: response.data.defaultPaymentMethod === 'CHECK',
            isAddressVerified: response.data.participationAddress?.isAddressVerified || false,
            isMailingAddressVerified: response.data.mailing?.isAddressVerified || false,
            contactName: response.data.contactName,
            notes: response.data.notes || response.data.comments?.[0]?.comment || '',
          };
          setLoadedFormSnapshot(initialFormValues);
          reset(initialFormValues as Parameters<typeof reset>[0]);
          // Re-mount AddressAutocomplete so it reflects server-loaded address line 1 consistently.
          setParticipationAddressKey((prev) => prev + 1);
          const nextDefaultPaymentMethod: DefaultPaymentMethod =
            response.data.defaultPaymentMethod === 'V'
              ? 'V'
              : response.data.defaultPaymentMethod === 'CHECK'
                ? 'CHECK'
                : 'ACH';
          setSelectedDefaultPaymentMethod(nextDefaultPaymentMethod);
        })
        .catch((error) => {
          setAlert((prev) => ({
            ...prev,
            message: error.response?.message || 'Failed to load account data',
            type: 'error',
          }));
          onCancel();
        })
        .finally(() => {
          setIsPageLoading(false);
        });
    }
  }, [accountId, reset, setAlert, refreshKey]);

  // Set address verification flags for validation
  useEffect(() => {
    const vendorVerified =
      participationAddressFromAutocomplete || participationAddressVerifiedByPopup;
    const checkVerified = mailingAddressFromAutocomplete || mailingAddressVerifiedByPopup;

    // Always update isAddressVerified when verification status changes
    if (formMethods.getValues('isAddressVerified') !== vendorVerified) {
      formMethods.setValue('isAddressVerified', vendorVerified);
    }

    // Always update isMailingAddressVerified when verification status changes
    if (formMethods.getValues('isMailingAddressVerified') !== checkVerified) {
      formMethods.setValue('isMailingAddressVerified', checkVerified);
    }
  }, [
    participationAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    participationAddressKeptAsEntered,
    mailingAddressFromAutocomplete,
    mailingAddressVerifiedByPopup,
    mailingAddressKeptAsEntered,
    formMethods,
  ]);

  // Synchronize mailing verification flags/message when participation address verification flags change and 'Same as Vendor Address' is checked
  useEffect(() => {
    if (sameAsVendorAddress) {
      setMailingAddressFromAutocomplete(participationAddressFromAutocomplete);
      setMailingAddressVerifiedByPopup(participationAddressVerifiedByPopup);
      setMailingAddressKeptAsEntered(participationAddressKeptAsEntered);
      if (participationAddressKeptAsEntered) {
        setOriginalMailingKeptAddress(
          `${participationAddress1},${participationAddress2}, ${participationCity}, ${participationState} ${participationZip}`,
        );
      } else {
        setOriginalMailingKeptAddress('');
      }
      // Set isMailingAddressVerified to match participation verification status
      const isVerified =
        participationAddressFromAutocomplete || participationAddressVerifiedByPopup;
      formMethods.setValue('isMailingAddressVerified', isVerified, { shouldValidate: true });

      // Update mailing baseline to match participation address
      setCurrentMailingBaseline({
        address1: participationAddress1 || '',
        address2: participationAddress2 || '',
        city: participationCity || '',
        state: participationState || '',
        zip: participationZip || '',
        isVerifiedByPopup: participationAddressVerifiedByPopup || false,
        isKeptAsEntered: participationAddressKeptAsEntered || false,
        isVerifiedFromAutocomplete: false,
      });
    }
  }, [
    sameAsVendorAddress,
    participationAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    participationAddressKeptAsEntered,
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
    formMethods,
  ]);

  // Synchronize check draft address fields when 'Same as Participation Address' is checked
  useEffect(() => {
    if (checkDraftSameAsParticipationAddress && isAddingCheck) {
      setIsSyncingCheckDraftFromParticipation(true);
      setCheckDraft((prev) => ({
        ...prev,
        address: participationAddress1 || '',
        addressLine2: participationAddress2 || '',
        city: participationCity || '',
        state: participationState || '',
        zip: participationZip || '',
      }));
      // Re-mount AddressAutocomplete so its internal input state reflects copied address line 1.
      setCheckDraftAddressKey((prev) => prev + 1);
      // Inherit the vendor address's verification status instead of assuming it is verified.
      // An unverified (e.g. incomplete) vendor address leaves the check address needing verification.
      setCheckDraftAddressVerified(
        (participationAddressFromAutocomplete || participationAddressVerifiedByPopup) &&
          !participationAddressChanged,
      );
      setCheckDraftAddressKeptAsEntered(
        participationAddressKeptAsEntered && !participationAddressChanged,
      );
    }
  }, [
    checkDraftSameAsParticipationAddress,
    isAddingCheck,
    participationAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    participationAddressKeptAsEntered,
    participationAddressChanged,
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
  ]);

  useEffect(() => {
    if (!isSyncingCheckDraftFromParticipation) return;

    if (!checkDraftSameAsParticipationAddress || !isAddingCheck) {
      setIsSyncingCheckDraftFromParticipation(false);
      return;
    }

    const isSynced = isCheckDraftAddressSameAsVendorAddress({
      address: checkDraft.address,
      addressLine2: checkDraft.addressLine2,
      city: checkDraft.city,
      state: checkDraft.state,
      zip: checkDraft.zip,
    });

    if (isSynced) {
      setIsSyncingCheckDraftFromParticipation(false);
    }
  }, [
    isSyncingCheckDraftFromParticipation,
    checkDraftSameAsParticipationAddress,
    isAddingCheck,
    checkDraft.address,
    checkDraft.addressLine2,
    checkDraft.city,
    checkDraft.state,
    checkDraft.zip,
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
  ]);

  useEffect(() => {
    if (!isAddingCheck || !!editingCheckId || isCheckDraftPayeeManuallyEdited) return;

    setCheckDraft((prev) => ({
      ...prev,
      payeeName: vendorName || '',
    }));
  }, [isAddingCheck, editingCheckId, isCheckDraftPayeeManuallyEdited, vendorName]);

  useEffect(() => {
    if (!isAddingCheck || !!editingCheckId || isCheckDraftRecipientManuallyEdited) return;

    setCheckDraft((prev) => ({
      ...prev,
      recipientName: vendorName || '',
    }));
  }, [
    isAddingCheck,
    editingCheckId,
    isCheckDraftRecipientManuallyEdited,
    vendorName,
  ]);

  useEffect(() => {
    if (activeTab !== 1) return;

    // Reset sensitive visibility state each time Status tab is opened.
    setIsAccountNumberStatusVisible(false);
    setIsStatusAccountNumberFetched(false);
    setRealAccountNumber('');
    void refreshStatusTabDataFromServer();
  }, [activeTab, accountId]);

  if (!accountId) {
    return (
      <Box className={classes.container}>
        <Box className={classes.contentContainer}>
          <Typography className={classes.title}>Account ID is required for editing</Typography>
        </Box>
      </Box>
    );
  }

  if (isPageLoading || !data) {
    return (
      <Box className={classes.loaderContainer}>
        <Loader colorType="warning" size={70} />
      </Box>
    );
  }

  const handleResendConfirmationClick = async (accountId: number) => {
    if (!accountId) return;
    setIsResendMailClicked(true);
    resendConfirmationEmail(accountId)
      .then((res) => {
        setAlert((prev) => ({
          ...prev,
          message: res.data.message || 'Confirmation email resent successfully.',
          type: 'success',
        }));
        setRefreshKey((prev) => prev + 1);
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Failed to resend confirmation email.',
          type: 'error',
        }));
      })
      .finally(() => {
        setIsResendMailClicked(false);
      });
  };

  const handleAdminConfirmationClick = async (accountId: number) => {
    if (!accountId) return;
    setIsAdminConfirmationClicked(true);
    adminConfirmationEmail(accountId)
      .then((res) => {
        setAlert((prev) => ({
          ...prev,
          message: res.data.message || 'Admin confirmation email sent successfully.',
          type: 'success',
        }));
        setRefreshKey((prev) => prev + 1);
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Failed to send admin confirmation email.',
          type: 'error',
        }));
      })
      .finally(() => {
        setIsAdminConfirmationClicked(false);
      });
  };

  const getAccountNumber = (isStatusTab = false) => {
    if (!accountId) return;
    if (isStatusTab) {
      // Toggle visibility
      setIsAccountNumberStatusVisible((prev) => {
        // If toggling to visible and not fetched, fetch and cache
        if (!prev && data?.bank?.hasAccountNumber && !isStatusAccountNumberFetched) {
          getAchAccountNumber(Number(accountId))
            .then((res) => {
              setRealAccountNumber(res.data.accountNumber); // update local state
              setIsStatusAccountNumberFetched(true);
            })
            .catch((error) => {
              setRealAccountNumber('');
              setIsStatusAccountNumberFetched(false);
              let errorMessage = 'Error fetching account number.';
              const axiosError = error as AxiosError<ErrorData>;
              if (axiosError?.isAxiosError) {
                errorMessage = axiosError.response?.data?.message ?? errorMessage;
              }
              setAlert((prev) => ({
                ...prev,
                message: errorMessage,
                type: 'error',
              }));
            });
        }
        return !prev;
      });
    } else {
      setIsAccountNumberVisible((prev) => {
        if (!prev && !isAccountNumberFetched) {
          if (data?.bank?.hasAccountNumber) {
            getAchAccountNumber(Number(accountId))
              .then((res) => {
                setValue('accountNumber', res.data.accountNumber); // updates the form field
                setRealAccountNumber(res.data.accountNumber); // updates local state
                setIsAccountNumberFetched(true); // marks as fetched
              })
              .catch((error) => {
                setRealAccountNumber('');
                setIsAccountNumberFetched(false);
                let errorMessage = 'Error fetching account number.';
                const axiosError = error as AxiosError<ErrorData>;
                if (axiosError?.isAxiosError) {
                  errorMessage = axiosError.response?.data?.message ?? errorMessage;
                }
                setAlert((prev) => ({
                  ...prev,
                  message: errorMessage,
                  type: 'error',
                }));
              });
          } else {
            // No account number to fetch, just mark as fetched so spinner doesn't show
            setIsAccountNumberFetched(true);
          }
        }
        return !prev;
      });
    }
  };

  // useEffect(() => {
  //   if (activeTab === 3 && accountId) {
  //     getVendorFBO(parseInt(accountId))
  //       .then((response) => {
  //         console.log('FBO response data:', response.data);
  //         setFboData(response.data);
  //       })
  //       .catch((error) => {
  //         setAlert((prev) => ({
  //           ...prev,
  //           message: error.response?.message || 'Failed to load FBO data',
  //           type: 'error',
  //         }));
  //       });
  //   }
  // }, [activeTab, accountId, refreshKey]);

  const handleRetryButtonClick = () => {
    if (accountId) {
      retryProvisioning(parseInt(accountId))
        .then((res) => {
          setAlert((prev) => ({
            ...prev,
            message: res.data.message || 'Provisioning retried successfully.',
            type: 'success',
          }));
          setRefreshKey((prev) => prev + 1);
        })
        .catch((error) => {
          setAlert((prev) => ({
            ...prev,
            message: error.response?.data.message || 'Error. Something went wrong...',
            type: 'error',
          }));
        });
    }
  };

  const handleViewChanges = () => {
    if (accountId) {
      setViewChangesPopupOpen(true);
    }
  };

  // NetSuite vendor sync handlers
  const handleSyncButtonClick = () => {
    setSyncAction('sync');
    setFindNetSuiteVendorPopupOpen(true);
  };

  const handleRelinkButtonClick = () => {
    setSyncAction('relink');
    setFindNetSuiteVendorPopupOpen(true);
  };

  const handleUnlinkButtonClick = () => {
    setUnlinkConfirmationOpen(true);
  };

  // Sync/relink directly from the review popup, no confirmation step
  const handleNetSuiteVendorSelect = async (vendorInternalId: string) => {
    if (!accountId || !vendorInternalId) return;

    try {
      const response = await setVendorExternalVendorId(parseInt(accountId), vendorInternalId);
      if (response.data.isSynced) {
        setAlert(() => ({
          message: `Vendor ${syncAction === 'sync' ? 'synced' : 'relinked'} successfully.`,
          type: 'success',
        }));
        setFindNetSuiteVendorPopupOpen(false);
      } else {
        setAlert(() => ({
          message: `Vendor ${syncAction === 'sync' ? 'sync' : 'relink'} failed .`,
          type: 'info',
        }));
      }
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert(() => ({
        message: axiosError.response?.data.message || `Failed to ${syncAction} vendor.`,
        type: 'error',
      }));
    }
  };

  const handleConfirmUnlink = async () => {
    if (!accountId) return;

    try {
      const response = await removeExternalVendorId(parseInt(accountId));
      setAlert(() => ({
        message: response.data.message || 'Vendor unlinked successfully.',
        type: 'success',
      }));
      setUnlinkConfirmationOpen(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert(() => ({
        message: axiosError.response?.data.message || 'Failed to unlink vendor.',
        type: 'error',
      }));
    }
  };

  const statusDefaultAchAccount =
    data.achAccounts?.find((account) => account.isDefault) || data.achAccounts?.[0] || null;
  const statusDefaultCheckAccount =
    data.checkAccounts?.find((account) => account.isDefault) || data.checkAccounts?.[0] || null;

  const statusRoutingNumber = statusDefaultAchAccount?.routingNumber || data.bank?.routingNumber || '';
  const statusCheckAddress = statusDefaultCheckAccount?.address
    ? `${statusDefaultCheckAccount.address}${statusDefaultCheckAccount.addressLine2 ? `, ${statusDefaultCheckAccount.addressLine2}` : ''}, ${statusDefaultCheckAccount.city ? `${statusDefaultCheckAccount.city},` : ''} ${statusDefaultCheckAccount.state ? `${statusDefaultCheckAccount.state} ` : ''}${statusDefaultCheckAccount.zip ? `${statusDefaultCheckAccount.zip}` : ''}`
    : data.mailing?.address1
      ? `${data.mailing.address1}${data.mailing.address2 ? `, ${data.mailing.address2}` : ''}, ${data.mailing.city ? `${data.mailing.city},` : ''} ${data.mailing.state ? `${data.mailing.state} ` : ''}${data.mailing.zip ? `${data.mailing.zip}` : ''}`
      : '';
  const statusCheckPhone = statusDefaultCheckAccount?.mailingPhoneNumber || data.mailing?.phoneNumber || '';

  return (
    <>
      {(isPaymentMethodsRefreshing || isFormSubmitting) && (
        <Box className={classes.blockingOverlay}>
          <Loader colorType="warning" size={70} />
        </Box>
      )}

      {/* Critical Error Banner for Passport Integration & TIN duplicate warnings */}
      {(!!data?.passportIntegration?.provisioningError ||
        !!(data?.warningMessages && data.warningMessages.length > 0)) && (
          <Box className={classes.errorMessageContainer}>
            {!!data?.passportIntegration?.provisioningError && (
              <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap">
                <Typography className={classes.errorMessageText}>
                  {data.passportIntegration.provisioningError}
                </Typography>
                {data?.passportIntegration?.retryAvailable && (
                  <Button
                    className={clsx(classes.retryButtonDefault, classes.saveButton)}
                    onClick={handleRetryButtonClick}
                    disabled={!canEdit}
                  >
                    {'Retry'}
                  </Button>
                )}
              </Box>
            )}
            {data?.warningMessages?.map((message, index) => (
              <Typography key={index} className={classes.warningMessageText}>
                {message}
              </Typography>
            ))}
          </Box>
        )}

      {/* Tabs */}
      <Box className={classes.tabsContainer} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Details" />
          <Tab label="Status" />
          <Tab label="Documents" />
          <Tab label="Payment History" />
          {/* <Tab label="FBO" /> */}
        </Tabs>
      </Box>

      <Box className={classes.formWrapper}>
        <FormProvider {...formMethods}>
          {(activeTab === 0 || activeTab === 1) && (
            <form className={clsx(classes.vendorEditForm, classes.formFieldSystem)}>
              {/* Details Tab */}
              {activeTab === 0 && (
                <>
                  {(() => {
                    const confirmationStatus = data?.vendorStatus === 'CONFIRMATION_REQUIRED_ADMIN';
                    if (confirmationStatus) {
                      return (
                        <Box className={classes.viewContainer}>
                          <Box className={classes.warningSection}>
                            <ErrorIcon sx={{ fontSize: '30px' }} color="secondary" />
                            <Typography
                              className={classes.warningText}
                              style={{ fontSize: '13px' }}
                            >
                              Changes detected from Vendor side.
                              <Typography
                                className={classes.warningText}
                                style={{ fontSize: '11px', marginTop: '4px' }}
                              >
                                Take a quick look and confirm, or compare data before
                                proceeding.
                              </Typography>
                            </Typography>
                          </Box>
                          <Button
                            className={clsx(classes.viewButton, classes.saveButton)}
                            onClick={handleViewChanges}
                          >
                            {'View Changes'}
                          </Button>
                        </Box>
                      );
                    }
                  })()}

                  <Box className={classes.formContainer}>
                    <Box display="flex" flexDirection="column" width="100%" maxWidth="470px">
                      <Typography className={classes.fieldHeadline}>Vendor Details</Typography>

                      <Box mt="16px">
                        <FormTextField
                          name="name"
                          label="Name *"
                          disabled={true}
                          InputProps={{
                            placeholder: 'Enter vendor name',
                          }}
                        />
                      </Box>

                      <Box mt="24px">
                        <FormSelectField
                          name="vendorType"
                          label="Vendor type *"
                          options={VENDOR_TYPE_OPTIONS}
                          width="100%"
                          disabled={true}
                        />
                      </Box>

                      <Box mt="24px" sx={{ position: 'relative' }}>
                        <FormMaskedTextField
                          mask="99-9999999"
                          name="tin"
                          label="EIN/TIN"
                          disabled={!canEdit}
                          InputProps={{
                            placeholder: 'Enter EIN/TIN',
                          }}
                        />
                      </Box>

                      <Box mt="24px">
                        <FormTextField
                          name="contactName"
                          label="Person to Contact *"
                          disabled={!canEdit}
                          InputProps={{
                            placeholder: 'Enter contact name',
                          }}
                        />
                      </Box>

                      <Box mt="24px">
                        <FormTextField
                          name="email"
                          label="Email Address *"
                          disabled={!canEdit}
                          InputProps={{
                            placeholder: 'email@example.com',
                          }}
                        />
                      </Box>

                      <Typography className={classes.fieldHeadline} mt="32px">
                        Address
                      </Typography>

                      {/* Address Verification Status */}
                      <Box mt="8px" display="flex" justifyContent="space-between">
                        {(participationAddressFromAutocomplete ||
                          participationAddressVerifiedByPopup) &&
                          !participationAddressChanged ? (
                          // Show green "Verified" when address is from autocomplete or verified by popup AND hasn't changed
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#4CAF50',
                              fontSize: '14px',
                              fontWeight: 500,
                              fontFamily: 'Inter',
                              padding: '5px',
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: '16px' }} />
                            <span>Admin side address verification completed</span>
                          </Box>
                        ) : participationAddressKeptAsEntered && !participationAddressChanged ? (
                          // Show warning icon with tooltip for "keep as entered" addresses that haven't changed
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#FF9800',
                              fontSize: '14px',
                              fontWeight: 500,
                              fontFamily: 'Inter',
                              padding: '5px',
                            }}
                          >
                            <WarningIcon sx={{ fontSize: '16px' }} />
                            <span>Vendor address not verified by USPS</span>
                          </Box>
                        ) : (
                          // Show orange "Verify" link when address is manually typed and mandatory fields are filled
                          participationAddress1 &&
                          participationCity &&
                          participationState &&
                          participationZip && (
                            <>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  color: '#FF9800',
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  fontFamily: 'Inter',
                                  padding: '5px',
                                }}
                              >
                                <ErrorIcon sx={{ fontSize: '16px' }} />
                                <span>Vendor address not verified. Please verify</span>
                              </Box>
                              {canEdit && (
                                <Box onClick={handleVerifyAddress} className={classes.verifyButton}>
                                  <span>Verify</span>
                                </Box>
                              )}
                            </>
                          )
                        )}
                      </Box>
                      <Box mt="16px">
                        <AddressAutocomplete
                          key={participationAddressKey}
                          name="participationAddress1"
                          label="Address Line 1"
                          optionsQuery={addressOptionsQuery}
                          mapQueryResults={mapAddressResults}
                          onValueChange={handleParticipationAddressSelect}
                          isDisableClearable={false}
                          addressType="Vendor Address"
                          disabled={!canEdit}
                          InputProps={{
                            value: participationAddress1 || '',
                            placeholder: 'Enter address line 1',
                          }}
                        />
                      </Box>

                      <Box mt="24px">
                        <FormTextField
                          name="participationAddress2"
                          label="Address Line 2"
                          disabled={!canEdit}
                          InputProps={{
                            placeholder: 'Enter address line 2 (optional)',
                          }}
                        />
                      </Box>

                      <Box mt="24px">
                        <FormTextField
                          name="participationCity"
                          label="City"
                          disabled={!canEdit}
                          InputProps={{
                            placeholder: 'Enter city',
                          }}
                        />
                      </Box>

                      <Box mt="24px">
                        <FormSelectField
                          name="participationState"
                          label="State"
                          options={US_STATES_LIST}
                          width="100%"
                          disabled={!canEdit}
                          shouldClearable={canEdit}
                        />
                      </Box>

                      <Box mt="24px">
                        <FormTextField
                          name="participationZip"
                          label="Zip"
                          disabled={!canEdit}
                          InputProps={{
                            placeholder: 'Enter ZIP code',
                          }}
                        />
                      </Box>

                      <Box mt="32px">
                        <FormCheckboxField
                          name="isVendorConfirmationRequired"
                          label="Need Vendor confirmation of Banking Details for provisioning."
                          disabled={isVendorConfirmationRequiredInitiallyTrue || !canEdit}
                        />
                      </Box>
                    </Box>

                    {/* Right Column - Payment Methods */}
                    <Box display="flex" flexDirection="column" width="100%" maxWidth="470px">
                      <Box className={classes.paymentMethodsPanel}>
                        <Box className={classes.paymentMethodsHeader}>
                          <Typography className={classes.fieldHeadline}>Payment Methods *</Typography>
                        </Box>

                        <Box className={classes.paymentMethodsStack}>
                          {/* ACH Accordion */}
                          <Accordion
                            className={clsx(
                              classes.accordion,
                              classes.paymentAccordion,
                              classes.paymentAccordionAch,
                            )}
                            expanded={achAccordionExpanded}
                            onChange={() => setAchAccordionExpanded(!achAccordionExpanded)}
                          >
                            <AccordionSummary
                              className={classes.accordionSummary}
                              expandIcon={
                                achAccordionExpanded ? (
                                  <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                                ) : (
                                  <ChevronRightIcon sx={{ color: '#ffffff' }} />
                                )
                              }
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                width="100%"
                              >
                                <Box className={classes.paymentSummaryTitleWrap}>
                                  <Typography className={classes.paymentSummaryTitle}>ACH</Typography>
                                  <Typography className={classes.paymentSummaryCount}>
                                    {displayAchAccounts.length === 0
                                      ? 'Not configured'
                                      : `${displayAchAccounts.length} account${displayAchAccounts.length === 1 ? '' : 's'}`}
                                  </Typography>
                                </Box>
                                <Box
                                  className={classes.paymentDefaultWrap}
                                  onClick={(e) => e.stopPropagation()} // Prevent accordion toggle for the entire box
                                >
                                  <Typography className={classes.paymentDefaultLabel}>Default</Typography>
                                  <Radio
                                    checked={selectedDefaultPaymentMethod === 'ACH'}
                                    onChange={(e) => {
                                      e.stopPropagation(); // Prevent accordion toggle
                                      if (isPaymentMethodsRefreshing) return;
                                      void handlePersistDefaultPaymentMethod('ACH');
                                      setAchAccordionExpanded(true);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation(); // Additional protection
                                      if (isPaymentMethodsRefreshing) return;
                                      setAchAccordionExpanded(true);
                                    }}
                                    className={classes.paymentDefaultRadio}
                                    disabled={!canEdit || isPaymentMethodsRefreshing || !hasAchAccountAvailable}
                                    size="small"
                                  />
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails
                              className={clsx(classes.accordionDetails, classes.paymentAccordionDetails)}
                            >
                              <Box display="flex" flexDirection="column" gap="12px">
                                <Box className={classes.paymentSectionHeader}>
                                  <Typography className={classes.paymentSectionLabel}>
                                    ACH Accounts
                                  </Typography>
                                  <Typography
                                    className={classes.paymentInlineActionToggle}
                                    onClick={() => {
                                      if (!canEdit || isPaymentMethodsRefreshing) return;
                                      if (isAddingAch) {
                                        setIsAddingAch(false);
                                        resetAchDraftForm();
                                        return;
                                      }
                                      setEditingAchId(null);
                                      setIsAddingAch(true);
                                      resetAchDraftForm();
                                    }}
                                    sx={{ opacity: canEdit ? 1 : 0.45 }}
                                  >
                                    {isAddingAch ? 'Hide form' : '+ Add Account'}
                                  </Typography>
                                </Box>
                                {isAddingAch && (
                                  <Box className={classes.paymentAddForm}>
                                    <Box className={classes.paymentAddRow}>
                                      <TextField
                                        id="vendor-edit-ach-nickname"
                                        label="Nickname *"
                                        value={achDraft.nickname}
                                        disabled={isPaymentMethodsRefreshing || isSavingAchAccount}
                                        onChange={(event) => {
                                          setAchDraft((prev) => ({
                                            ...prev,
                                            nickname: event.target.value,
                                          }));
                                          setAchDraftTouched((prev) => ({ ...prev, nickname: true }));
                                        }}
                                        onBlur={() =>
                                          setAchDraftTouched((prev) => ({ ...prev, nickname: true }))
                                        }
                                        error={
                                          shouldShowAchDraftError('nickname') &&
                                          Boolean(achDraftErrors.nickname)
                                        }
                                        helperText={
                                          shouldShowAchDraftError('nickname') && achDraftErrors.nickname
                                            ? achDraftErrors.nickname
                                            : ''
                                        }
                                        FormHelperTextProps={{
                                          id: 'vendor-edit-ach-nickname-helper',
                                          sx: {
                                            color:
                                              shouldShowAchDraftError('nickname') && achDraftErrors.nickname
                                                ? theme.palette.error.main
                                                : 'text.secondary',
                                            fontFamily: 'Inter',
                                            fontSize: '12px',
                                            lineHeight: '18px',
                                            marginTop: '6px',
                                            marginLeft: 0,
                                          },
                                        }}
                                        InputProps={{
                                          placeholder: 'e.g. Billing office',
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              <Tooltip title="Shown in the payment dropdown" arrow placement="top">
                                                <IconButton
                                                  size="small"
                                                  className={classes.infoIconButton}
                                                  tabIndex={-1}
                                                  aria-label="Nickname helper"
                                                >
                                                  <InfoOutlinedIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            </InputAdornment>
                                          ),
                                        }}
                                        className={classes.paymentAddInput}
                                      />
                                      <TextField
                                        select
                                        label="Account Type *"
                                        value={achDraft.accountType}
                                        disabled={isPaymentMethodsRefreshing || isSavingAchAccount}
                                        onChange={(event) =>
                                          setAchDraft((prev) => ({
                                            ...prev,
                                            accountType: event.target.value,
                                          }))
                                        }
                                        SelectProps={{
                                          MenuProps: {
                                            MenuListProps: {
                                              sx: {
                                                backgroundColor: theme.palette.secondary.dark,
                                                color: '#FFFFFF',
                                                '& .MuiMenuItem-root': {
                                                  color: '#FFFFFF',
                                                  fontFamily: 'Inter',
                                                  fontSize: '14px',
                                                  fontWeight: 500,
                                                },
                                                '& .MuiMenuItem-root:hover': {
                                                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                                },
                                                '& .MuiMenuItem-root.Mui-selected': {
                                                  backgroundColor: 'rgba(255, 255, 255, 0.16)',
                                                },
                                                '& .MuiMenuItem-root.Mui-selected:hover': {
                                                  backgroundColor: 'rgba(255, 255, 255, 0.22)',
                                                },
                                              },
                                            },
                                          },
                                        }}
                                        className={classes.paymentAddInput}
                                      >
                                        <MenuItem value="Checking">Checking</MenuItem>
                                        <MenuItem value="Savings">Savings</MenuItem>
                                      </TextField>
                                    </Box>
                                    {isValidRoutingNumber(achDraft.routingNumber) &&
                                      (achRoutingBankName || achRoutingLookupError) &&
                                      (achRoutingLookupError ? (
                                        <Typography
                                          sx={{
                                            color: theme.palette.warning.light,
                                            fontFamily: 'Inter',
                                            fontSize: '12px',
                                            lineHeight: '18px',
                                            marginTop: '2px',
                                            marginBottom: '2px',
                                          }}
                                        >
                                          {ROUTING_LOOKUP_UNAVAILABLE_MESSAGE}
                                        </Typography>
                                      ) : (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            marginTop: '2px',
                                            marginBottom: '2px',
                                          }}
                                        >
                                          <CheckCircleIcon
                                            sx={{ color: theme.palette.success.light, fontSize: '16px' }}
                                          />
                                          <Typography
                                            sx={{
                                              color: theme.palette.success.light,
                                              fontFamily: 'Inter',
                                              fontSize: '12px',
                                              lineHeight: '18px',
                                            }}
                                          >
                                            {achRoutingBankName}
                                          </Typography>
                                        </Box>
                                      ))}
                                    <TextField
                                      label="Account number *"
                                      value={achDraft.accountNumber}
                                      disabled={isPaymentMethodsRefreshing || isSavingAchAccount}
                                      onChange={(event) => {
                                        setAchDraft((prev) => ({
                                          ...prev,
                                          accountNumber: event.target.value,
                                        }));
                                        setAchDraftTouched((prev) => ({ ...prev, accountNumber: true }));
                                      }}
                                      onBlur={() =>
                                        setAchDraftTouched((prev) => ({ ...prev, accountNumber: true }))
                                      }
                                      error={
                                        shouldShowAchDraftError('accountNumber') &&
                                        Boolean(achDraftErrors.accountNumber)
                                      }
                                      helperText={
                                        shouldShowAchDraftError('accountNumber')
                                          ? achDraftErrors.accountNumber
                                          : ''
                                      }
                                      FormHelperTextProps={{
                                        sx: {
                                          fontFamily: 'Inter',
                                          fontSize: '12px',
                                          lineHeight: '18px',
                                          marginTop: '6px',
                                          marginLeft: 0,
                                        },
                                      }}
                                      className={classes.paymentAddInput}
                                    />
                                    <TextField
                                      label="Routing number *"
                                      value={achDraft.routingNumber}
                                      disabled={isPaymentMethodsRefreshing || isSavingAchAccount}
                                      onChange={(event) => {
                                        handleAchRoutingNumberChange(event.target.value);
                                        setAchDraftTouched((prev) => ({ ...prev, routingNumber: true }));
                                      }}
                                      onBlur={(event) => {
                                        setAchDraftTouched((prev) => ({ ...prev, routingNumber: true }));
                                        void handleAchRoutingNumberBlur(event.target.value);
                                      }}
                                      error={
                                        shouldShowAchDraftError('routingNumber') &&
                                        Boolean(achDraftErrors.routingNumber)
                                      }
                                      inputProps={{ maxLength: 9 }}
                                      helperText={
                                        shouldShowAchDraftError('routingNumber')
                                          ? achDraftErrors.routingNumber
                                          : ''
                                      }
                                      FormHelperTextProps={{
                                        sx: {
                                          color:
                                            shouldShowAchDraftError('routingNumber') &&
                                              achDraftErrors.routingNumber
                                              ? theme.palette.error.main
                                              : undefined,
                                          fontFamily: 'Inter',
                                          fontSize: '12px',
                                          lineHeight: '18px',
                                          marginTop: '6px',
                                          marginLeft: 0,
                                        },
                                      }}
                                      className={classes.paymentAddInput}
                                    />
                                    <Box className={classes.paymentAddActions}>
                                      <Button
                                        className={classes.paymentAddCancel}
                                        onClick={() => {
                                          if (isPaymentMethodsRefreshing || isSavingAchAccount) return;
                                          setIsAddingAch(false);
                                          resetAchDraftForm();
                                        }}
                                        disabled={isPaymentMethodsRefreshing || isSavingAchAccount}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        className={classes.paymentAddPrimary}
                                        onClick={handleAddAchAccount}
                                        disabled={
                                          hasAchDraftErrors || isPaymentMethodsRefreshing || isSavingAchAccount
                                        }
                                      >
                                        {isSavingAchAccount ? (
                                          <>
                                            <CircularProgress size={14} sx={{ color: 'inherit', mr: 1 }} />
                                            {editingAchId ? 'Saving...' : 'Adding...'}
                                          </>
                                        ) : editingAchId ? (
                                          'Save Account'
                                        ) : (
                                          'Add Account'
                                        )}
                                      </Button>
                                    </Box>
                                  </Box>
                                )}
                                {visibleAchAccounts.map((account) => (
                                  (() => {
                                    const isAchBaseActionEnabled =
                                      canEdit &&
                                      !isPaymentMethodsRefreshing &&
                                      editingAchId !== account.id;
                                    const isAchEditEnabled =
                                      isAchBaseActionEnabled && !isAchAccountActionBlocked(account);
                                    const isAchSetDefaultEnabled =
                                      isAchBaseActionEnabled && !isAchAccountActionBlocked(account);
                                    const isAchDeleteEnabled = isAchBaseActionEnabled;

                                    return (
                                      <Box key={account.id} className={classes.paymentAccountCard}>
                                        <Box className={classes.paymentCardHeader}>
                                          <Typography className={classes.paymentCardTitle}>
                                            {account.nickname}
                                          </Typography>
                                          <Box className={classes.paymentStatusRow}>
                                            <StatusChip
                                              label={ProvisioningStatusMap[account.provisionStatus].label}
                                              color={ProvisioningStatusMap[account.provisionStatus].color}
                                            />
                                            {account.isDefault && (
                                              <Box className={classes.defaultMethodPill}>
                                                Default Account
                                              </Box>
                                            )}
                                          </Box>
                                        </Box>
                                        <Typography className={classes.paymentCardText}>
                                          {account.accountType}
                                        </Typography>
                                        <Typography className={classes.paymentCardText}>
                                          {account.maskedAccountNumber}
                                        </Typography>
                                        <Typography className={classes.paymentCardText}>
                                          {account.routingNumber ? `Routing: ${account.routingNumber}` : 'Routing: --'}
                                        </Typography>
                                        {shouldShowProvisionError(account.provisionStatus) &&
                                          !!account.provisionError && (
                                            <Typography className={classes.paymentProvisionError}>
                                              {account.provisionError}
                                            </Typography>
                                          )}
                                        <AchEwsNote
                                          provisionStatus={account.provisionStatus}
                                          ewsStatus={account.ewsStatus}
                                        />
                                        {account.isDefault ? (
                                          <Box className={classes.paymentCardActions}>
                                            <Typography
                                              className={classes.paymentInlineAction}
                                              onClick={() =>
                                                isAchEditEnabled && handleStartEditAchAccount(account)
                                              }
                                              sx={{
                                                opacity: isAchEditEnabled ? 1 : 0.45,
                                              }}
                                            >
                                              Edit
                                            </Typography>
                                            <Typography
                                              className={classes.paymentInlineAction}
                                              onClick={() =>
                                                isAchDeleteEnabled && handleRequestDeleteAchAccount(account.id)
                                              }
                                              sx={{
                                                opacity: isAchDeleteEnabled ? 1 : 0.45,
                                              }}
                                            >
                                              Delete
                                            </Typography>
                                          </Box>
                                        ) : (
                                          <Box className={classes.paymentCardActions}>
                                            <Typography
                                              className={classes.paymentInlineAction}
                                              onClick={() =>
                                                isAchSetDefaultEnabled &&
                                                handleSetDefaultAchAccount(account.id)
                                              }
                                              sx={{
                                                opacity: isAchSetDefaultEnabled ? 1 : 0.45,
                                              }}
                                            >
                                              Set default
                                            </Typography>
                                            <Typography
                                              className={classes.paymentInlineAction}
                                              onClick={() =>
                                                isAchEditEnabled && handleStartEditAchAccount(account)
                                              }
                                              sx={{
                                                opacity: isAchEditEnabled ? 1 : 0.45,
                                              }}
                                            >
                                              Edit
                                            </Typography>
                                            <Typography
                                              className={classes.paymentInlineAction}
                                              onClick={() =>
                                                isAchDeleteEnabled && handleRequestDeleteAchAccount(account.id)
                                              }
                                              sx={{
                                                opacity: isAchDeleteEnabled ? 1 : 0.45,
                                              }}
                                            >
                                              Delete
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })()
                                ))}
                                {displayAchAccounts.length > DEFAULT_VISIBLE_ACCOUNTS && (
                                  <Typography
                                    className={classes.paymentMoreToggle}
                                    onClick={() => setShowAllAchAccounts((prev) => !prev)}
                                  >
                                    {showAllAchAccounts ? 'Show less' : `Show +${hiddenAchCount} more`}
                                  </Typography>
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>

                          {/* CHECK Accordion */}
                          <Accordion
                            className={clsx(
                              classes.accordion,
                              classes.paymentAccordion,
                              classes.paymentAccordionCheck,
                            )}
                            expanded={checkAccordionExpanded}
                            onChange={() => setCheckAccordionExpanded(!checkAccordionExpanded)}
                          >
                            <AccordionSummary
                              className={classes.accordionSummary}
                              expandIcon={
                                checkAccordionExpanded ? (
                                  <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                                ) : (
                                  <ChevronRightIcon sx={{ color: '#ffffff' }} />
                                )
                              }
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                width="100%"
                              >
                                <Box className={classes.paymentSummaryTitleWrap}>
                                  <Typography className={classes.paymentSummaryTitle}>CHECK</Typography>
                                  <Typography className={classes.paymentSummaryCount}>
                                    {displayCheckAccounts.length === 0
                                      ? 'Not configured'
                                      : `${displayCheckAccounts.length} account${displayCheckAccounts.length === 1 ? '' : 's'}`}
                                  </Typography>
                                </Box>
                                <Box
                                  className={classes.paymentDefaultWrap}
                                  onClick={(e) => e.stopPropagation()} // Prevent accordion toggle for the entire box
                                >
                                  <Typography className={classes.paymentDefaultLabel}>Default</Typography>
                                  <Radio
                                    checked={selectedDefaultPaymentMethod === 'CHECK'}
                                    onChange={(e) => {
                                      e.stopPropagation(); // Prevent accordion toggle
                                      if (isPaymentMethodsRefreshing) return;
                                      void handlePersistDefaultPaymentMethod('CHECK');
                                      setCheckAccordionExpanded(true);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation(); // Additional protection
                                      if (isPaymentMethodsRefreshing) return;
                                      setCheckAccordionExpanded(true);
                                    }}
                                    className={classes.paymentDefaultRadio}
                                    disabled={!canEdit || isPaymentMethodsRefreshing || !hasCheckAddressAvailable}
                                    size="small"
                                  />
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails
                              className={clsx(classes.accordionDetails, classes.paymentAccordionDetails)}
                            >
                              <Box display="flex" flexDirection="column" gap="12px">
                                <Box className={classes.paymentSectionHeader}>
                                  <Typography className={classes.paymentSectionLabel}>
                                    Mailing Addresses
                                  </Typography>
                                  <Typography
                                    className={classes.paymentInlineActionToggle}
                                    onClick={() => {
                                      if (!canEdit || isPaymentMethodsRefreshing) return;
                                      if (isAddingCheck) {
                                        setIsAddingCheck(false);
                                        resetCheckDraftForm();
                                        return;
                                      }
                                      setEditingCheckId(null);
                                      setIsAddingCheck(true);
                                      resetCheckDraftForm();
                                    }}
                                    sx={{ opacity: canEdit ? 1 : 0.45 }}
                                  >
                                    {isAddingCheck ? 'Hide form' : '+ Add Address'}
                                  </Typography>
                                </Box>
                                {isAddingCheck && (
                                  <Box className={classes.paymentAddForm}>
                                    <TextField
                                      id="vendor-edit-check-nickname"
                                      label="Nickname *"
                                      value={checkDraft.nickname}
                                      disabled={isPaymentMethodsRefreshing || isSavingCheckAddress}
                                      onChange={(event) => {
                                        setCheckDraftTouched((prev) => ({ ...prev, nickname: true }));
                                        setCheckDraft((prev) => ({
                                          ...prev,
                                          nickname: event.target.value,
                                        }));
                                      }
                                      }
                                      onBlur={() =>
                                        setCheckDraftTouched((prev) => ({ ...prev, nickname: true }))
                                      }
                                      error={Boolean(shouldShowCheckDraftError('nickname') && checkDraftErrors.nickname)}
                                      helperText={
                                        shouldShowCheckDraftError('nickname') ? checkDraftErrors.nickname : ''
                                      }
                                      FormHelperTextProps={{
                                        sx: { marginTop: '6px', marginLeft: 0, minHeight: '18px', lineHeight: '18px' },
                                      }}
                                      InputProps={{
                                        placeholder: 'e.g. Billing office',
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <Tooltip title="Shown in the payment dropdown" arrow placement="top">
                                              <IconButton
                                                size="small"
                                                className={classes.infoIconButton}
                                                tabIndex={-1}
                                                aria-label="Nickname helper"
                                              >
                                                <InfoOutlinedIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </InputAdornment>
                                        ),
                                      }}
                                      className={classes.paymentAddInput}
                                    />
                                    <TextField
                                      id="vendor-edit-check-payee"
                                      label="Payee name"
                                      value={checkDraft.payeeName}
                                      disabled={isPaymentMethodsRefreshing || isSavingCheckAddress}
                                      onChange={(event) => {
                                        setIsCheckDraftPayeeManuallyEdited(true);
                                        setCheckDraft((prev) => ({
                                          ...prev,
                                          payeeName: event.target.value,
                                        }));
                                      }}
                                      InputProps={{
                                        placeholder: 'Defaults to vendor name',
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <Tooltip
                                              title={'Prints on the check "Pay to the order of"'}
                                              arrow
                                              placement="top"
                                            >
                                              <IconButton
                                                size="small"
                                                className={classes.infoIconButton}
                                                tabIndex={-1}
                                                aria-label="Payee name helper"
                                              >
                                                <InfoOutlinedIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </InputAdornment>
                                        ),
                                      }}
                                      className={classes.paymentAddInput}
                                    />
                                    <TextField
                                      id="vendor-edit-check-recipient"
                                      label="Recipient name"
                                      value={checkDraft.recipientName}
                                      disabled={isPaymentMethodsRefreshing || isSavingCheckAddress}
                                      onChange={(event) => {
                                        setIsCheckDraftRecipientManuallyEdited(true);
                                        setCheckDraft((prev) => ({
                                          ...prev,
                                          recipientName: event.target.value,
                                        }));
                                      }}
                                      InputProps={{
                                        placeholder: 'Defaults to payee',
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <Tooltip
                                              title="Who receives the mailed check, e.g. a collection agency"
                                              arrow
                                              placement="top"
                                            >
                                              <IconButton
                                                size="small"
                                                className={classes.infoIconButton}
                                                tabIndex={-1}
                                                aria-label="Recipient name helper"
                                              >
                                                <InfoOutlinedIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </InputAdornment>
                                        ),
                                      }}
                                      className={classes.paymentAddInput}
                                    />
                                    <TextField
                                      label="Mobile Number *"
                                      value={checkDraft.mailingPhoneNumber}
                                      onFocus={() => {
                                        if (!checkDraft.mailingPhoneNumber) {
                                          setCheckDraft((prev) => ({
                                            ...prev,
                                            mailingPhoneNumber: '+1-',
                                          }));
                                        }
                                      }}
                                      onBlur={() => {
                                        if (checkDraft.mailingPhoneNumber === '+1-') {
                                          setCheckDraft((prev) => ({
                                            ...prev,
                                            mailingPhoneNumber: '',
                                          }));
                                        }
                                        setCheckDraftTouched((prev) => ({ ...prev, mailingPhoneNumber: true }));
                                      }}
                                      onChange={(event) =>
                                        setCheckDraft((prev) => ({
                                          ...prev,
                                          mailingPhoneNumber: formatUsPhoneInput(event.target.value),
                                        }))
                                      }
                                      disabled={!canEdit || isPaymentMethodsRefreshing || isSavingCheckAddress}
                                      error={Boolean(
                                        shouldShowCheckDraftError('mailingPhoneNumber') &&
                                        checkDraftErrors.mailingPhoneNumber,
                                      )}
                                      helperText={
                                        shouldShowCheckDraftError('mailingPhoneNumber')
                                          ? checkDraftErrors.mailingPhoneNumber
                                          : ''
                                      }
                                      FormHelperTextProps={{
                                        sx: { marginTop: '6px', marginLeft: 0, minHeight: '18px', lineHeight: '18px' },
                                      }}
                                      InputProps={{
                                        placeholder: '+1-555-123-4567',
                                      }}
                                      inputProps={{ maxLength: 16 }}
                                      className={classes.paymentAddInput}
                                    />
                                    <FormControlLabel
                                      className={classes.checkboxWrapper}
                                      control={
                                        <Checkbox
                                          icon={<CheckBoxIcon />}
                                          checkedIcon={<CheckedCheckBoxIcon />}
                                          disableRipple
                                          checked={checkDraftSameAsParticipationAddress}
                                          disabled={
                                            isPaymentMethodsRefreshing ||
                                            isSavingCheckAddress ||
                                            (!hasParticipationAddress &&
                                              !checkDraftSameAsParticipationAddress)
                                          }
                                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const isChecked = e.target.checked;
                                            setCheckDraftSameAsParticipationAddress(isChecked);
                                            setIsSyncingCheckDraftFromParticipation(isChecked);
                                          }}
                                        />
                                      }
                                      label="Same as Vendor Address"
                                    />
                                    <AddressAutocomplete
                                      key={`check-draft-address-${checkDraftAddressKey}`}
                                      name={`checkDraftAddressLine1_${checkDraftAddressKey}`}
                                      label="Address line 1 *"
                                      width="100%"
                                      disabled={isPaymentMethodsRefreshing || isSavingCheckAddress}
                                      optionsQuery={addressOptionsQuery}
                                      mapQueryResults={mapAddressResults}
                                      InputProps={{
                                        value: checkDraft.address,
                                        placeholder: 'Enter address line 1',
                                      }}
                                      error={Boolean(shouldShowCheckDraftError('address') && checkDraftErrors.address)}
                                      helperText={
                                        shouldShowCheckDraftError('address') ? checkDraftErrors.address : ''
                                      }
                                      onValueChange={(addressData) =>
                                        handleCheckDraftAddressSelect(addressData as AddressPrediction)
                                      }
                                    />
                                    <TextField
                                      label="Address line 2"
                                      value={checkDraft.addressLine2}
                                      disabled={isPaymentMethodsRefreshing || isSavingCheckAddress}
                                      onChange={(event) => {
                                        const nextAddressLine2 = event.target.value;
                                        const nextAddressValues = {
                                          address: checkDraft.address,
                                          addressLine2: nextAddressLine2,
                                          city: checkDraft.city,
                                          state: checkDraft.state,
                                          zip: checkDraft.zip,
                                        };

                                        if (
                                          !isSyncingCheckDraftFromParticipation &&
                                          checkDraftSameAsParticipationAddress &&
                                          !isCheckDraftAddressSameAsVendorAddress(nextAddressValues)
                                        ) {
                                          setCheckDraftSameAsParticipationAddress(false);
                                        }

                                        setCheckDraftAddressKeptAsEntered(false);
                                        setCheckDraft((prev) => ({
                                          ...prev,
                                          addressLine2: nextAddressLine2,
                                        }));
                                      }}
                                      className={classes.paymentAddInput}
                                    />
                                    <Box className={classes.paymentAddRow}>
                                      <TextField
                                        label="City *"
                                        value={checkDraft.city}
                                        disabled={isPaymentMethodsRefreshing || isSavingCheckAddress}
                                        onChange={(event) => {
                                          const nextCity = event.target.value;
                                          const nextAddressValues = {
                                            address: checkDraft.address,
                                            addressLine2: checkDraft.addressLine2,
                                            city: nextCity,
                                            state: checkDraft.state,
                                            zip: checkDraft.zip,
                                          };

                                          if (
                                            !isSyncingCheckDraftFromParticipation &&
                                            checkDraftSameAsParticipationAddress &&
                                            !isCheckDraftAddressSameAsVendorAddress(nextAddressValues)
                                          ) {
                                            setCheckDraftSameAsParticipationAddress(false);
                                          }

                                          setCheckDraftAddressVerified(false);
                                          setCheckDraftAddressKeptAsEntered(false);
                                          setCheckDraft((prev) => ({
                                            ...prev,
                                            city: nextCity,
                                          }));
                                          setCheckDraftTouched((prev) => ({ ...prev, city: true }));
                                        }}
                                        error={Boolean(shouldShowCheckDraftError('city') && checkDraftErrors.city)}
                                        helperText={
                                          shouldShowCheckDraftError('city') ? checkDraftErrors.city : ''
                                        }
                                        FormHelperTextProps={{
                                          sx: { marginTop: '6px', marginLeft: 0, minHeight: '18px', lineHeight: '18px' },
                                        }}
                                        className={classes.paymentAddInput}
                                      />
                                      <TextField
                                        select
                                        label="State *"
                                        value={checkDraft.state}
                                        disabled={isPaymentMethodsRefreshing || isSavingCheckAddress}
                                        SelectProps={{
                                          MenuProps: {
                                            PaperProps: {
                                              sx: {
                                                backgroundColor: 'secondary.dark',
                                                color: 'common.white',
                                              },
                                            },
                                            MenuListProps: {
                                              sx: {
                                                backgroundColor: 'secondary.dark',
                                                color: 'common.white',
                                              },
                                            },
                                          },
                                        }}
                                        onChange={(event) => {
                                          const nextState = event.target.value;
                                          const nextAddressValues = {
                                            address: checkDraft.address,
                                            addressLine2: checkDraft.addressLine2,
                                            city: checkDraft.city,
                                            state: nextState,
                                            zip: checkDraft.zip,
                                          };

                                          if (
                                            !isSyncingCheckDraftFromParticipation &&
                                            checkDraftSameAsParticipationAddress &&
                                            !isCheckDraftAddressSameAsVendorAddress(nextAddressValues)
                                          ) {
                                            setCheckDraftSameAsParticipationAddress(false);
                                          }

                                          setCheckDraftAddressVerified(false);
                                          setCheckDraftAddressKeptAsEntered(false);
                                          setCheckDraft((prev) => ({
                                            ...prev,
                                            state: nextState,
                                          }));
                                          setCheckDraftTouched((prev) => ({ ...prev, state: true }));
                                        }}
                                        error={Boolean(shouldShowCheckDraftError('state') && checkDraftErrors.state)}
                                        helperText={
                                          shouldShowCheckDraftError('state') ? checkDraftErrors.state : ''
                                        }
                                        FormHelperTextProps={{
                                          sx: {
                                            marginTop: '6px',
                                            marginLeft: 0,
                                            minHeight: '18px',
                                            lineHeight: '18px',
                                          },
                                        }}
                                        className={classes.paymentAddInput}
                                      >
                                        {US_STATES_LIST.map((stateItem) => (
                                          <MenuItem key={stateItem.id} value={stateItem.id}>
                                            {stateItem.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Box>
                                    <TextField
                                      label="Zip *"
                                      value={checkDraft.zip}
                                      disabled={isPaymentMethodsRefreshing || isSavingCheckAddress}
                                      onChange={(event) => {
                                        const nextZip = event.target.value;
                                        const nextAddressValues = {
                                          address: checkDraft.address,
                                          addressLine2: checkDraft.addressLine2,
                                          city: checkDraft.city,
                                          state: checkDraft.state,
                                          zip: nextZip,
                                        };

                                        if (
                                          !isSyncingCheckDraftFromParticipation &&
                                          checkDraftSameAsParticipationAddress &&
                                          !isCheckDraftAddressSameAsVendorAddress(nextAddressValues)
                                        ) {
                                          setCheckDraftSameAsParticipationAddress(false);
                                        }

                                        setCheckDraftAddressVerified(false);
                                        setCheckDraftAddressKeptAsEntered(false);
                                        setCheckDraft((prev) => ({
                                          ...prev,
                                          zip: nextZip,
                                        }));
                                        setCheckDraftTouched((prev) => ({ ...prev, zip: true }));
                                      }}
                                      error={Boolean(shouldShowCheckDraftError('zip') && checkDraftErrors.zip)}
                                      helperText={
                                        shouldShowCheckDraftError('zip') ? checkDraftErrors.zip : ''
                                      }
                                      FormHelperTextProps={{
                                        sx: { marginTop: '6px', marginLeft: 0, minHeight: '18px', lineHeight: '18px' },
                                      }}
                                      className={classes.paymentAddInput}
                                    />

                                    {(checkDraft.address.trim() ||
                                      checkDraft.city.trim() ||
                                      checkDraft.state.trim() ||
                                      checkDraft.zip.trim()) && (
                                        <Box mt="2px" display="flex" justifyContent="space-between" alignItems="center" gap="8px">
                                          {checkDraftAddressVerified ? (
                                            <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              color: '#4CAF50',
                                              fontSize: '14px',
                                              fontWeight: 500,
                                              fontFamily: 'Inter',
                                              padding: '5px',
                                            }}
                                            >
                                              <CheckCircleIcon sx={{ fontSize: '16px' }} />
                                              <span>Admin side address verification completed</span>
                                            </Box>
                                          ) : checkDraftAddressKeptAsEntered ? (
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                color: '#FF9800',
                                                fontSize: '12px',
                                                fontFamily: 'Inter',
                                              }}
                                            >
                                              <WarningIcon sx={{ fontSize: '14px' }} />
                                              <span>Vendor address not verified by USPS</span>
                                            </Box>
                                          ) : (
                                            <>
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: '4px',
                                                  color: '#FF9800',
                                                  fontSize: '12px',
                                                  fontFamily: 'Inter',
                                                }}
                                              >
                                                <WarningIcon sx={{ fontSize: '14px' }} />
                                                <span>Please verify the address</span>
                                              </Box>
                                              <Box
                                                onClick={() => !isCheckDraftAddressVerifying && handleVerifyCheckDraftAddress()}
                                                className={classes.verifyButton}
                                                sx={{
                                                  padding: '4px 10px',
                                                  borderRadius: '7px',
                                                  fontSize: '12px',
                                                  opacity:
                                                    isCheckDraftAddressVerifying ||
                                                      isSavingCheckAddress ||
                                                      isPaymentMethodsRefreshing
                                                      ? 0.7
                                                      : 1,
                                                  pointerEvents:
                                                    isCheckDraftAddressVerifying ||
                                                      isSavingCheckAddress ||
                                                      isPaymentMethodsRefreshing
                                                      ? 'none'
                                                      : 'auto',
                                                }}
                                              >
                                                <span>{isCheckDraftAddressVerifying ? 'Verifying...' : 'Verify'}</span>
                                              </Box>
                                            </>
                                          )}
                                        </Box>
                                      )}
                                    <Box className={classes.paymentAddActions}>
                                      <Button
                                        className={classes.paymentAddCancel}
                                        onClick={() => {
                                          if (isPaymentMethodsRefreshing || isSavingCheckAddress) return;
                                          setIsAddingCheck(false);
                                          resetCheckDraftForm();
                                        }}
                                        disabled={isPaymentMethodsRefreshing || isSavingCheckAddress}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        className={classes.paymentAddPrimary}
                                        onClick={handleAddCheckAddress}
                                        disabled={
                                          isPaymentMethodsRefreshing ||
                                          isSavingCheckAddress ||
                                          !isCheckDraftFormValid
                                        }
                                      >
                                        {isSavingCheckAddress ? (
                                          <>
                                            <CircularProgress size={14} sx={{ color: 'inherit', mr: 1 }} />
                                            {editingCheckId ? 'Saving...' : 'Adding...'}
                                          </>
                                        ) : editingCheckId ? (
                                          'Save Address'
                                        ) : (
                                          'Add Address'
                                        )}
                                      </Button>
                                    </Box>
                                  </Box>
                                )}
                                {checkAccountsForCards.map((account) => {
                                  const isCheckActionEnabled =
                                    canEdit &&
                                    !isPaymentMethodsRefreshing &&
                                    editingCheckId !== account.id;

                                  return (
                                    <Box key={account.id} className={classes.paymentAccountCard}>
                                      <Box className={classes.paymentCardHeader}>
                                        <Typography className={classes.paymentCardTitle}>
                                          {account.nickname}
                                        </Typography>
                                        <Box className={classes.paymentStatusRow}>
                                          <StatusChip
                                            label={ProvisioningStatusMap[account.provisionStatus].label}
                                            color={ProvisioningStatusMap[account.provisionStatus].color}
                                          />
                                          {account.isDefault && (
                                            <Box className={classes.defaultMethodPill}>
                                              Default Address
                                            </Box>
                                          )}
                                        </Box>
                                      </Box>
                                      <Typography className={classes.paymentCardText}>
                                        {account.address}
                                      </Typography>
                                      {!!account.addressLine2 && (
                                        <Typography className={classes.paymentCardText}>
                                          {account.addressLine2}
                                        </Typography>
                                      )}
                                      <Typography className={classes.paymentCardText}>
                                        {account.city} {account.state} {account.zip}
                                      </Typography>
                                      {(account.payeeName || account.recipientName) && (
                                        <Typography className={classes.paymentCardText}>
                                          {account.payeeName || '--'} / {account.recipientName || '--'}
                                        </Typography>
                                      )}
                                      {account.mailingPhoneNumber && (
                                        <Typography className={classes.paymentCardText}>
                                          {account.mailingPhoneNumber}
                                        </Typography>
                                      )}
                                      {shouldShowProvisionError(account.provisionStatus) &&
                                        !!account.provisionError && (
                                          <Typography className={classes.paymentProvisionError}>
                                            {account.provisionError}
                                          </Typography>
                                        )}

                                      {account.isDefault && (
                                        <>
                                          <Box className={classes.paymentCardActions}>
                                            <Typography
                                              className={classes.paymentInlineAction}
                                              onClick={() =>
                                                isCheckActionEnabled &&
                                                handleStartEditCheckAddress(account)
                                              }
                                              sx={{
                                                opacity: isCheckActionEnabled ? 1 : 0.45,
                                                cursor: isCheckActionEnabled ? 'pointer' : 'default',
                                                pointerEvents: isCheckActionEnabled ? 'auto' : 'none',
                                              }}
                                            >
                                              Edit
                                            </Typography>
                                            <Typography
                                              className={classes.paymentInlineAction}
                                              onClick={() =>
                                                isCheckActionEnabled &&
                                                handleRequestDeleteCheckAccount(account.id)
                                              }
                                              sx={{
                                                opacity: isCheckActionEnabled ? 1 : 0.45,
                                                cursor: isCheckActionEnabled ? 'pointer' : 'default',
                                                pointerEvents: isCheckActionEnabled ? 'auto' : 'none',
                                              }}
                                            >
                                              Delete
                                            </Typography>
                                          </Box>
                                        </>
                                      )}
                                      {!account.isDefault && (
                                        <Box className={classes.paymentCardActions}>
                                          <Typography
                                            className={classes.paymentInlineAction}
                                            onClick={() =>
                                              isCheckActionEnabled &&
                                              handleSetDefaultCheckAccount(account.id)
                                            }
                                            sx={{
                                              opacity: isCheckActionEnabled ? 1 : 0.45,
                                              cursor: isCheckActionEnabled ? 'pointer' : 'default',
                                              pointerEvents: isCheckActionEnabled ? 'auto' : 'none',
                                            }}
                                          >
                                            Set default
                                          </Typography>
                                          <Typography
                                            className={classes.paymentInlineAction}
                                            onClick={() =>
                                              isCheckActionEnabled &&
                                              handleStartEditCheckAddress(account)
                                            }
                                            sx={{
                                              opacity: isCheckActionEnabled ? 1 : 0.45,
                                              cursor: isCheckActionEnabled ? 'pointer' : 'default',
                                              pointerEvents: isCheckActionEnabled ? 'auto' : 'none',
                                            }}
                                          >
                                            Edit
                                          </Typography>
                                          <Typography
                                            className={classes.paymentInlineAction}
                                            onClick={() =>
                                              isCheckActionEnabled &&
                                              handleRequestDeleteCheckAccount(account.id)
                                            }
                                            sx={{
                                              opacity: isCheckActionEnabled ? 1 : 0.45,
                                              cursor: isCheckActionEnabled ? 'pointer' : 'default',
                                              pointerEvents: isCheckActionEnabled ? 'auto' : 'none',
                                            }}
                                          >
                                            Delete
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                                {displayCheckAccounts.length > DEFAULT_VISIBLE_ACCOUNTS && (
                                  <Typography
                                    className={classes.paymentMoreToggle}
                                    onClick={() => setShowAllCheckAccounts((prev) => !prev)}
                                  >
                                    {showAllCheckAccounts ? 'Show less' : `Show +${hiddenCheckCount} more`}
                                  </Typography>
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>

                          {/* V Payment Method */}
                          <Accordion
                            className={clsx(
                              classes.accordion,
                              classes.paymentAccordion,
                              classes.paymentAccordionUpcoming,
                            )}
                            expanded={false}
                            disabled
                            sx={{
                              '&.Mui-disabled': {
                                opacity: 1,
                                backgroundColor: 'transparent',
                              },
                              '& .MuiAccordionSummary-root.Mui-disabled': {
                                opacity: 1,
                                backgroundColor: 'transparent',
                              },
                              backgroundColor: 'transparent',
                            }}
                          >
                            <AccordionSummary className={classes.accordionSummary}>
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                width="100%"
                              >
                                <Box className={classes.paymentSummaryTitleWrap}>
                                  <Typography className={classes.paymentSummaryTitle}>V</Typography>
                                  {/* <Typography className={classes.paymentSummaryMeta}>
                                  Virtual Card Rail
                                </Typography> */}
                                </Box>
                                <Box
                                  className={classes.paymentDefaultWrap}
                                >
                                  <Typography className={classes.paymentDefaultLabel}>
                                    Default
                                  </Typography>
                                  <Radio
                                    checked={selectedDefaultPaymentMethod === 'V'}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                    }}
                                    className={classes.paymentDefaultRadio}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled
                                    size="small"
                                  />
                                </Box>
                              </Box>
                            </AccordionSummary>
                          </Accordion>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box className={classes.divider} />

                  <Box display="flex" flexDirection="column" width="100%" mt={2} mb={2}>
                    <Typography className={classes.fieldHeadline}>Notes</Typography>


                    <Box mt="24px">
                      {canEdit ? (
                        <>
                          <FormTextField
                            name="notes"
                            label="Add notes about this vendor"
                            isMultiline
                            isDescription
                            InputProps={{
                              inputProps: { maxLength: 2000 }, // important: nested like this
                              placeholder: 'Add notes about this vendor',
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <Box
                            sx={{
                              color: theme.palette.text.secondary,
                              fontFamily: 'Inter',
                              fontSize: '16px',
                              fontWeight: '500',
                              lineHeight: '120%',
                              letterSpacing: '0px',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              cursor: 'default',
                              background: 'rgba(255, 255, 255, 0.03)',
                              borderRadius: '12px',
                              border: `1px solid ${theme.palette.secondary.dark}`,
                              padding: '13.5px 16px',
                              minHeight: '104px',
                            }}
                          >
                            {notesValue.trim() ? notesValue : 'No notes.'}
                          </Box>
                        </>
                      )}
                    </Box>

                    <Box
                      mt="8px"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="baseline"
                      flexWrap="wrap"
                      gap="8px"
                    >
                      {data?.comments?.[0] ? (
                        <Box display="flex" alignItems="baseline" gap="4px" flexWrap="wrap">
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '16px',
                              color: 'rgba(255,255,255,0.5)',
                              fontFamily: 'Inter',
                              fontWeight: 500,
                            }}
                          >
                            Updated by
                          </Typography>
                          <InactiveUserName isInactive={data.comments[0].addedByIsDeactivated}>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '16px',
                                color: '#FFFFFF',
                              fontFamily: 'Inter',
                              fontWeight: 600,
                            }}
                          >
                            {data.comments[0].addedByName}
                          </Typography>
                          </InactiveUserName>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '16px',
                              color: 'rgba(255,255,255,0.65)',
                              fontFamily: 'Inter',
                              fontWeight: 500,
                            }}
                          >
                            {data.comments[0].addedByRole} on {moment(data.comments[0].addedAt).format('MMM DD, YYYY, h:mm A')}
                          </Typography>
                        </Box>
                      ) : (
                        <Box />
                      )}
                      {canEdit && (
                        <Typography
                          sx={{
                            color: theme.palette.text.secondary,
                            fontFamily: 'Inter',
                            fontSize: '16px',
                            fontWeight: '500',
                            lineHeight: '120%',
                            letterSpacing: '0px',
                          }}
                        >
                          {notesValue.length}/2000
                        </Typography>
                      )}
                    </Box>

                    {(data?.comments?.length ?? 0) > 1 && (
                      <Box display="inline-flex" mt="10px">
                        <Typography
                          onClick={() => setShowCommentHistory((prev) => !prev)}
                          sx={{
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#FFFFFF',
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            userSelect: 'none',
                            width: 'fit-content',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {showCommentHistory ? 'Hide comment history' : 'Show comment history'}
                        </Typography>
                      </Box>
                    )}

                    {showCommentHistory && (data?.comments?.length ?? 0) > 1 && (
                      <VendorCommentsSection comments={(data?.comments ?? []).slice(1)} />
                    )}
                  </Box>

                  {canEdit && (
                    <Box display="flex" width="100%" justifyContent="center">
                      <Box
                        display="flex"
                        mt="32px"
                        maxWidth="416px"
                        flexDirection="column"
                        gap="16px"
                        width="100%"
                      >
                        <FormSubmitButton
                          disabled={
                            isFormSubmitting ||
                            !isValid ||
                            !hasChanges ||
                            // An empty or partial vendor address is allowed and has nothing to verify
                            (isParticipationAddressComplete && isParticipationAddressNeedsVerification) ||
                            isMailingAddressNeedsVerification
                          }
                          buttonIcon={<SaveIcon />}
                          buttonText={'Save'}
                          onClick={handleSubmit(onSubmitForm)}
                        />
                        <FormCancelButton onClick={onCancel} />
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {/* Status Tab */}
              {activeTab === 1 && (
                <Box className={classes.tabContent}>
                  <Box className={classes.statusContainer}>
                    <Box className={classes.statusSection}>
                      <Typography variant="h6" sx={{ color: 'white', mb: 3, fontWeight: 600 }}>
                        ACH
                      </Typography>
                      {data?.bank?.integration?.provisioning && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography sx={{ color: 'white', minWidth: '120px', fontWeight: 500 }}>
                              Provisioning
                            </Typography>
                            <StatusChip
                              label={
                                ProvisioningStatusMap[data.bank.integration.provisioning.status]
                                  .label
                              }
                              color={
                                ProvisioningStatusMap[data.bank.integration.provisioning.status]
                                  .color
                              }
                            />
                          </Box>
                          {data.bank.integration.provisioning.error?.message && (
                            <Typography
                              sx={{
                                color: 'error.main',
                                fontSize: '14px',
                                mt: 1,
                                fontFamily: 'Inter',
                              }}
                            >
                              Error: {data.bank.integration.provisioning.error.message}
                            </Typography>
                          )}

                          {data.bank.integration.provisioning.error?.retryAvailable && (
                            <Box>
                              <Button
                                className={clsx(classes.buttonDefault, classes.saveButton)}
                                onClick={handleRetryButtonClick}
                                disabled={!canEdit}
                              >
                                {'Retry'}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                      {data?.bank?.integration?.externalAccount && (
                        <>
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Typography
                                sx={{ color: 'white', minWidth: '120px', fontWeight: 500 }}
                              >
                                Account Status
                              </Typography>
                              {data?.bank?.integration?.externalAccount?.status && (
                                <StatusChip
                                  label={
                                    ExternalAccountStatusMap[
                                      data.bank.integration.externalAccount.status
                                    ].label
                                  }
                                  color={
                                    ExternalAccountStatusMap[
                                      data.bank.integration.externalAccount.status
                                    ].color
                                  }
                                />
                              )}
                            </Box>
                          </Box>

                          {data?.bank?.integration?.externalAccount?.ewsValidation && (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Typography
                                  sx={{ color: 'white', minWidth: '120px', fontWeight: 500 }}
                                >
                                  EWS Status
                                </Typography>
                                <StatusChip
                                  label={
                                    EwsValidationStatusMap[
                                      data.bank.integration.externalAccount.ewsValidation.status
                                    ].label
                                  }
                                  color={
                                    EwsValidationStatusMap[
                                      data.bank.integration.externalAccount.ewsValidation.status
                                    ].color
                                  }
                                />
                              </Box>
                            </>
                          )}
                        </>
                      )}
                      {data?.bank?.name && (
                        <Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}
                            >
                              Bank Name
                            </Typography>
                            <Typography sx={{ color: 'white', fontWeight: 500 }}>
                              {data.bank.name}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {data?.bank?.hasAccountNumber && (
                        <Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}
                            >
                              Account Number:
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography sx={{ color: 'white', fontWeight: 500 }}>
                                {isAccountNumberStatusVisible ? (
                                  !isStatusAccountNumberFetched ? (
                                    <Box
                                      display={'flex'}
                                      alignItems={'center'}
                                      justifyContent={'center'}
                                      padding={'8px'}
                                    >
                                      <CircularProgress size={24} />
                                    </Box>
                                  ) : (
                                    realAccountNumber || accountNumberStatus
                                  )
                                ) : (
                                  getMaskedAccountNumber()
                                )}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => getAccountNumber(true)}
                                sx={{ color: 'white' }}
                              >
                                {isAccountNumberStatusVisible ? (
                                  <VisibilityOffIcon fontSize="small" />
                                ) : (
                                  <VisibilityIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      )}
                      {statusRoutingNumber && (
                        <Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}
                            >
                              Routing Number:
                            </Typography>
                            <Typography sx={{ color: 'white', fontWeight: 500 }}>
                              {statusRoutingNumber}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    <Box className={classes.statusSection}>
                      <Typography variant="h6" sx={{ color: 'white', mb: 3, fontWeight: 600 }}>
                        CHECK
                      </Typography>
                      {data?.mailing?.provisioning && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography sx={{ color: 'white', minWidth: '120px', fontWeight: 500 }}>
                              Provisioning
                            </Typography>
                            <StatusChip
                              label={ProvisioningStatusMap[data.mailing.provisioning.status].label}
                              color={ProvisioningStatusMap[data.mailing.provisioning.status].color}
                            />
                          </Box>
                          {data.mailing.provisioning.error?.message && (
                            <Typography
                              sx={{
                                color: 'error.main',
                                fontSize: '14px',
                                mt: 1,
                                fontFamily: 'Inter',
                              }}
                            >
                              Error: {data.mailing.provisioning.error.message}
                            </Typography>
                          )}
                          {data.mailing.provisioning.error?.retryAvailable && (
                            <Box>
                              <Button
                                className={clsx(classes.buttonDefault, classes.saveButton)}
                                onClick={handleRetryButtonClick}
                                disabled={!canEdit}
                              >
                                {'Retry'}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                      {data?.mailing && (
                        <>
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Typography
                                sx={{ color: 'white', minWidth: '120px', fontWeight: 500 }}
                              >
                                Account Status
                              </Typography>
                              {data?.mailing?.status && (
                                <StatusChip
                                  label={
                                    ExternalAccountStatusMap[data.mailing.status]?.label ||
                                    data.mailing.status
                                  }
                                  color={
                                    ExternalAccountStatusMap[data.mailing.status]?.color ||
                                    'default'
                                  }
                                />
                              )}
                            </Box>

                            {statusCheckAddress && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  sx={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontSize: '14px',
                                    mb: 0.5,
                                  }}
                                >
                                  Address:
                                </Typography>
                                <Typography sx={{ color: 'white', fontWeight: 500 }}>
                                  {statusCheckAddress}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {statusCheckPhone && (
                            <Box>
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  sx={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontSize: '14px',
                                    mb: 0.5,
                                  }}
                                >
                                  Phone:
                                </Typography>
                                <Typography sx={{ color: 'white', fontWeight: 500 }}>
                                  {statusCheckPhone}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* NetSuite Link Section - Separate Card */}
                  {data?.accounting && (
                    <Box
                      sx={{
                        border: '1px solid #5e6174',
                        borderRadius: '8px',
                        padding: '16px',
                        mt: 3,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <LinkIcon sx={{ fontSize: 20, color: 'white' }} />
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                          {vendorEntity} Link
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography sx={{ color: 'white', minWidth: '120px', fontWeight: 500 }}>
                            Status
                          </Typography>
                          <StatusChip
                            label={data.accounting.status}
                            color={data.accounting.isSynced ? 'success' : 'error'}
                          />
                        </Box>
                      </Box>

                      {data.accounting.externalVendorId && (
                        <Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}
                            >
                              {vendorEntity} Entity ID
                            </Typography>
                            {data.accounting.link ? (
                              <Typography
                                component="a"
                                href={data.accounting.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: 'white',
                                  fontWeight: 500,
                                  textDecoration: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  width: 'fit-content',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {data.accounting.externalVendorId}
                                <LaunchIcon sx={{ fontSize: 20 }} />
                              </Typography>
                            ) : (
                              <Typography sx={{ color: 'white', fontWeight: 500 }}>
                                {data.accounting.externalVendorId}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}

                      {data.accounting.lastSyncedAt && (
                        <Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}
                            >
                              Last Synced
                            </Typography>
                            <Typography sx={{ color: 'white', fontWeight: 500 }}>
                              {new Date(data.accounting.lastSyncedAt).toLocaleString('en-US', {
                                month: 'numeric',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
                        {!data.accounting.isSynced ? (
                          <Button
                            variant="contained"
                            onClick={handleSyncButtonClick}
                            disabled={!canEdit}
                            startIcon={<SyncIcon />}
                            sx={{
                              backgroundColor: theme.palette.info.main,
                              color: 'white',
                              textTransform: 'none',
                              fontSize: '14px',
                              fontWeight: 500,
                              padding: '8px 16px',
                              '&:hover': {
                                backgroundColor: theme.palette.info.main,
                              },
                              '&:disabled': {
                                backgroundColor: 'rgba(45, 212, 191, 0.3)',
                                color: 'rgba(255, 255, 255, 0.5)',
                              },
                            }}
                          >
                            Sync
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="contained"
                              onClick={handleRelinkButtonClick}
                              disabled={!canEdit}
                              startIcon={<LinkIcon />}
                              sx={{
                                backgroundColor: theme.palette.info.main,
                                color: 'white',
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                padding: '8px 16px',
                                '&:hover': {
                                  backgroundColor: theme.palette.info.main,
                                },
                                '&:disabled': {
                                  backgroundColor: 'rgba(45, 212, 191, 0.3)',
                                  color: 'rgba(255, 255, 255, 0.5)',
                                },
                              }}
                            >
                              Relink
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={handleUnlinkButtonClick}
                              disabled={!canEdit}
                              startIcon={<LinkOffIcon />}
                              sx={{
                                borderColor: theme.palette.error.main,
                                color: theme.palette.error.main,
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                padding: '8px 16px',
                                '&:hover': {
                                  borderColor: theme.palette.error.main,
                                  backgroundColor: 'transparent',
                                },
                                '&:disabled': {
                                  borderColor: 'rgba(211, 47, 47, 0.3)',
                                  color: 'rgba(211, 47, 47, 0.5)',
                                },
                              }}
                            >
                              Unlink
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </form>
          )}

          {/* FBO Tab */}
          {/* {activeTab === 2 && (
            <Box className={classes.tabContentFbo}>
              <Box className={classes.statusContainer}>
                <Box className={classes.statusSection}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                      Vendor FBO
                    </Typography>

                    {fboData?.accounts?.case?.status && (
                      <StatusChip
                        label={FBOAccountStatusMap[fboData.accounts.case.status as keyof typeof FBOAccountStatusMap].label}
                        color={FBOAccountStatusMap[fboData.accounts.case.status as keyof typeof FBOAccountStatusMap].color}
                      />
                    )}
                  </Box>

                  {fboData?.accounts?.case?.accountNumber && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', mb: 0.5 }}>
                          Account Number : {fboData.accounts.case.accountNumber}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {fboData?.accounts?.case?.balance && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                          {numberToUSDCurrency(fboData.accounts.case.balance)}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                </Box>
              </Box>
            </Box>
          )} */}

          {activeTab === 2 && (
            <Box className={classes.tabContent}>
              <VendorDocumentTab
                participationId={Number(accountId)}
              />
            </Box>
          )}

          {/* Actions Container */}
          {activeTab === 0 && canEdit && (
            <Box className={classes.actionsContainer}>
              <Typography className={classes.actionsHeading}>Actions</Typography>
              <Box className={classes.actionButtonsWrapper}>
                <CommonButton
                  type="infoMain"
                  buttonText="Resend Mail"
                  maxWidth="auto"
                  onButtonClick={() => {
                    handleResendConfirmationClick(Number(accountId));
                  }}
                  disabled={!isResendMailActive || isResendMailClicked}
                />
                <CommonButton
                  type="infoDark"
                  buttonText="Confirm"
                  maxWidth="auto"
                  onButtonClick={() => {
                    handleAdminConfirmationClick(Number(accountId));
                  }}
                  disabled={
                    !isAdminConfirmationRequired || hasChanges || isAdminConfirmationClicked
                  }
                />
                {/* <CommonButton
                  type="infoDark"
                  buttonText="Archive"
                  maxWidth="auto"
                  onButtonClick={() => console.log('archive')}
                  disabled={true}
                /> */}
              </Box>
            </Box>
          )}

          {activeTab === 3 && (
            <PaymentHistoryTable vendorId={Number(accountId)} />
          )

          }
        </FormProvider>
      </Box>

      <VendorConfirmationPopup
        open={vendorConfirmationOpen}
        addressType={
          addressVerificationType === 'participation'
            ? 'Vendor Address'
            : addressVerificationType === 'mailing'
              ? 'Check Mailing Address'
              : 'Check Address'
        }
        enteredAddress={
          addressVerificationType === 'participation'
            ? participationAddress1
              ? `${participationAddress1},${participationAddress2}, ${participationCity}, ${participationState} ${participationZip}`
              : ''
            : addressVerificationType === 'mailing'
              ? mailingAddress1
                ? `${mailingAddress1}, ${mailingAddress2}, ${mailingCity}, ${mailingState} ${mailingZip}`
                : ''
              : checkDraft.address
                ? `${checkDraft.address}, ${checkDraft.addressLine2}, ${checkDraft.city}, ${checkDraft.state} ${checkDraft.zip}`
                : ''
        }
        onConfirm={handleVendorConfirmation}
        onCancel={() => setVendorConfirmationOpen(false)}
        onVendorSelect={handleVendorOptionSelect}
      />
      {historyData && (
        <ViewVendorChangesPopup
          open={viewChangesPopupOpen}
          onClose={() => setViewChangesPopupOpen(false)}
          data={historyData}
        // accountNumber={realAccountNumber}
        />
      )}

      {/* NetSuite Vendor Sync Popup */}
      <FindNetSuiteVendorPopup
        open={findNetSuiteVendorPopupOpen}
        lienName={data?.name || ''}
        searchTerm={data?.name || ''}
        onClose={() => setFindNetSuiteVendorPopupOpen(false)}
        onVendorIdSelect={handleNetSuiteVendorSelect}
        lienId={accountId ? Number(accountId) : 0}
        entityType="Vendor"
        entityId={accountId ? Number(accountId) : 0}
        syncAction={syncAction}
      />

      {/* Unlink Confirmation Popup */}
      <ConfirmationPopup
        open={unlinkConfirmationOpen}
        title="Confirm Unlink"
        message={`Are you sure you want to unlink this Vendor ${data?.name} from ${vendorEntity}? This action will remove the connection between V.Law and ${vendorEntity} for this vendor.`}
        confirmText="Unlink"
        onConfirm={handleConfirmUnlink}
        onCancel={() => setUnlinkConfirmationOpen(false)}
      />

      <ConfirmationPopup
        open={paymentMethodDeleteConfirmationOpen}
        title="Confirm Delete"
        message={
          pendingDeletePaymentMethod?.type === 'check'
            ? 'Are you sure you want to delete this check address?'
            : 'Are you sure you want to delete this ACH account?'
        }
        confirmText="Delete"
        confirmButtonClassName={clsx(
          classes.confirmPopupActionButton,
          classes.confirmPopupDeleteButton,
        )}
        cancelButtonClassName={clsx(
          classes.confirmPopupActionButton,
          classes.confirmPopupCancelButton,
        )}
        onConfirm={handleConfirmDeletePaymentMethod}
        onCancel={handleCancelDeletePaymentMethod}
      />      
    </>
  );
};

export default VendorEditFormContainer;
